import lazyWithModuleBoundary from '../../utils/lazyWithModuleBoundary';
import { useImportFileTransfer } from '../../context/ImportFileTransferContext';
import WatchlistPendingCallout from './WatchlistPendingCallout';
import WatchlistPublicationRecoveryNotice from './WatchlistPublicationRecoveryNotice';
import { WATCHLIST_DRAFT_GUIDANCE_EVENT, showWatchlistDraftGuidance } from '../../utils/watchlistDraftGuidance';
import React, {
    useState,
    useMemo,
    useEffect,
    useRef,
    useCallback,
} from 'react';
import {
    Alert,
    Box,
    Button,
    Typography,
    CircularProgress,
    Chip,
    Divider,
    Grid2,
    Paper,
    Popover,
    Snackbar,
    Tooltip,
    IconButton,
    TextField,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HistoryBookIcon from '../icons/HistoryBookIcon';
import WatchlistMarketStatusChip from './WatchlistMarketStatusChip';
import WatchlistMarketHealthChip from './WatchlistMarketHealthChip';
import {
    latestMarketHealth,
    MARKET_HEALTH_LABELS,
    marketHealthDistressed,
    marketHealthSeverity,
    marketHealthStatus,
} from '../../utils/marketHealth';
import { HeaderTitle } from './grid/WatchlistGridHeader';
import { marketDataUpdateTooltip, marketMetricExplanation, marketMetricQualifier, selectMarketMetric } from '../../utils/marketMetricProvenance';
import { appBarActionButtonSx, appBarDividerSx } from '../../styles/msiAppBarStyles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import { constructSortFn, functionalUpdate } from '@tanstack/react-table';
import { Pencil, Trash2, Undo2 } from 'lucide-react';
import {
    getWatchlistMarketStatusLabel,
    getWatchlistMarketStatusTooltip,
    isWatchlistMarketDataPending,
    isWatchlistMarketDataRetrying,
    isWatchlistMarketDataUnavailable,
} from '../../utils/marketDataStatus';
import BusyOverlay from '../commonComponents/BusyOverlay';
// The grid's cells and headers use the Tailwind kit; the MUI imports above
// serve the toolbar, dialogs and popovers around it.
import Checkbox from '../ui/Checkbox';
import KitChip from '../ui/Chip';
import KitIconButton from '../ui/IconButton';
import KitTooltip from '../ui/Tooltip';
import Spinner from '../ui/Spinner';
import { cx } from '../ui/classNames';
import { createPortfolioAttemptId, isValidPortfolioQuantity } from '../../utils/portfolioEditorDraft';
import PortfolioPositionsDialog from '../risk_overview/PortfolioPositionsDialog';
import * as XLSX from 'xlsx';
import {
    fetchWatchlistResolutionSummary,
    fetchWatchlistFieldEditor,
    applyWatchlistAttributeMutations,
    stageWatchlistPortfolioEditor,
    stageSecurityResolutionRemovals,
} from '../../utils/ApiCall';
import { parseCloudRunError } from '../msiDriftReviewHelpers';
import {
    APPBAR_UPLOAD_MODAL_OPEN_EVENT,
} from '../dashboard/dashboardUtils';
import SecurityOperationProgress from './SecurityOperationProgress';
import {
    WATCHLIST_DELTA_CLASSIFICATIONS,
    WATCHLIST_HISTORY_OPEN_EVENT,
    dispatchMsiSecurityResolutionAppliedEvent,
    isSecurityOperationEventForScope,
    scopeSecurityOperationEventDetail,
} from '../../utils/securityResolutionEvents';
import { useSecurityOperationContext } from '../../context/SecurityOperationContext';
import {
    parseWatchlistPublicationReceipt,
} from '../../utils/watchlistPublication';
import { normalizeWatchlistLifecycle } from '../../utils/watchlistLifecycle';
import WatchlistHistoryPanel from './WatchlistHistoryPanel';
import { cardClasses } from '../../styles/tailwindClasses';
import NewsCell from '../watchlist_news/NewsCell';
import {
    watchlistPanelSx,
    watchlistPopoverPaperSx,
} from '../../styles/watchlistMsiStyles';
import { cellEditorFieldSx } from '../../styles/cellEditingStyles';
import WatchlistGrid from './grid/WatchlistGrid';
import { sortByNumber, sortByText } from '../data_table/sorting';
import useViewportFillHeight, { gridFrameHeight } from './grid/useViewportFillHeight';
import {
    EMPTY_DRAFT_HISTORY,
    applyDraftChanges,
    buildDraftAction,
    filterDraftHistory,
    recordDraftAction,
    takeRedo,
    takeUndo,
} from './spreadsheet/draftHistory';
import {
    EMPTY_PICKS,
    hasStalePicks,
    isPickHistoryAction,
    markStalePicksInHistory,
} from './add_security/pendingAdditions';
import { stagePicks } from './add_security/stagePicks';
import useAddSecurityGridRows from './add_security/useAddSecurityGridRows';
import { isMacPlatform } from './spreadsheet/keyboardCommands';
import { parseTsv } from './spreadsheet/clipboardTsv';
import CategoryPill from './spreadsheet/CategoryPill';
import CategoryColumnHeader, { CategoryColumnsContext } from './spreadsheet/CategoryColumnHeader';
import useCategoryColumnTools from './spreadsheet/useCategoryColumnTools';
import SecurityDetailsPopover from '../security_details/SecurityDetailsPopover';
import WatchlistAddSecurityDialog from './WatchlistAddSecurityDialog';
import {
    OVERALL_STATUS_RUNNING,
    deriveSecurityOperationOverallStatus,
    shouldShowSecurityOperationBanner,
} from '../../utils/securityOperationStatus';
import {
    SECURITY_IDENTITY_FIELD_OPTIONS,
    securityCompanyName,
    securityExportIdentity,
    securityIdentityDetails,
    securityIdentityPresentation,
} from '../../utils/securityIdentityPresentation';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from '../commonComponents/SecurityIdentityFieldSelector';
import { CountryFlagText } from '../shared/securityDisplayPrimitives';
import WatchlistAttributeManagerDialog from './WatchlistAttributeManagerDialog';
import useWatchlistOwnGroupPreferences from './useWatchlistOwnGroupPreferences';
import { buildOwnGroupColumns } from './WatchlistOwnGroupStatistics';
import { isSystemCountryDefinition } from './watchlistAttributeDefinitions';
import { isWatchlistGroupingAttribute } from '../../utils/watchlistAttributePresentation';
import {
    workspaceActionClusterSx,
} from '../../styles/workspaceChromeStyles';
import DashboardToolbarPortal from '../dashboard/components/DashboardToolbarPortal';
import WatchlistGetStartedCallout from './WatchlistGetStartedCallout';
import WatchlistQuickFilter from './WatchlistQuickFilter';
import { filterWatchlistRows } from './watchlistRowSearch';
import { isPastedSheet, pastedSheetFile } from './pastedSheet';
import {
    PASTE_SHORTCUT_LABEL,
    PasteFromExcelDialog,
    usePagePaste,
} from '../shared/SheetPaste';
import { PAGE_PASTE_HINT, WatchlistEmptyPasteHint } from './WatchlistSheetPaste';
import WatchlistReturnsBasisSelector, {
    TRAILING_RETURN_WINDOWS,
    WatchlistReturnsColumnHeader,
    persistReturnsBasis,
    readPersistedReturnsBasis,
    returnsFieldName,
    withReturnsExportColumns,
} from './WatchlistReturnsBasis';

const WatchlistAttributeAiDialog = lazyWithModuleBoundary(() => import('./WatchlistAttributeAiDialog'));
const WatchlistOwnGroupStatistics = lazyWithModuleBoundary(() => import('./WatchlistOwnGroupStatisticsQuery'));
const WATCHLIST_SORT_STORAGE_KEY_PREFIX = 'watchlist-table-sort-model';
const WATCHLIST_RETURNS_SELECTOR_ID = 'watchlist-returns-basis-selector';
const REMOVED_WATCHLIST_IDENTITY_SORT_FIELDS = new Set([
    'display_identifier',
    'msi_company_name',
    'ticker',
]);
const pinnedIdentityFields = ['in_actual_portfolio', 'identity_summary'];
export const WATCHLIST_IDENTITY_FIELD_OPTIONS = SECURITY_IDENTITY_FIELD_OPTIONS;
// An empty Watchlist's frame: rows of room for the add row and the paste invitation.
const EMPTY_WATCHLIST_ROW_COUNT = 14;
const WATCHLIST_LOADING_HEIGHT = 560;
const WATCHLIST_COLUMN_GROUP_START_CLASS = 'watchlist-col-group-start';
const WATCHLIST_ATTRIBUTE_FIELD_PREFIX = 'watchlist_attribute:';
const UNDO_SHORTCUT_LABEL = isMacPlatform() ? '⌘Z' : 'Ctrl+Z';
const REDO_SHORTCUT_LABEL = isMacPlatform() ? '⇧⌘Z' : 'Ctrl+Y';
// Market column chip labels; the copy text (meta.formatText) uses the same words.
const MARKET_STATUS_TEXT = {
    open: 'Open',
    closed: 'Closed',
    searching: 'Searching…',
    fetching: 'Searching…',
    retrying: 'Retrying',
    unavailable: 'Unavailable',
};
const WATCHLIST_COLUMN_GROUP_CLASSES = {
    identity: 'watchlist-col-group-identity',
    market: 'watchlist-col-group-market',
    risk: 'watchlist-col-group-risk',
    returns: 'watchlist-col-group-returns',
};

function watchlistFieldText(value) {
    return value == null ? '' : String(value);
}

// The grid's id of a Watchlist row.
function watchlistGridRowId(row) {
    return String(row.id);
}

function joinClassNames(...classNames) {
    return classNames.filter(Boolean).join(' ');
}

export function getWatchlistPinnedIdentityFields() {
    return pinnedIdentityFields;
}

function watchlistColumnGroupClass(group, { start = false } = {}) {
    return joinClassNames(
        WATCHLIST_COLUMN_GROUP_CLASSES[group],
        start ? WATCHLIST_COLUMN_GROUP_START_CLASS : null,
    );
}

/**
 * A comparator to sort numeric columns by absolute value in ascending order.
 * The grid automatically flips the order when the user sets descending sort.
 */
function numericSortComparator(v1, v2) {
    const raw1 = v1 == null ? 0 : v1;
    const raw2 = v2 == null ? 0 : v2;
    const n1 = parseFloat(raw1) || 0;
    const n2 = parseFloat(raw2) || 0;
    return Math.abs(n1) - Math.abs(n2);
}

function timestampSortComparator(v1, v2) {
    const left = Number(v1);
    const right = Number(v2);
    const safeLeft = Number.isFinite(left) ? left : 0;
    const safeRight = Number.isFinite(right) ? right : 0;
    return safeLeft - safeRight;
}

const MARKET_STATUS_SORT_RANK = {
    open: 0,
    closed: 1,
    searching: 2,
    fetching: 3,
    retrying: 4,
    unavailable: 5,
    awaiting: 6,
};

// Column sort functions (ascending; the grid reverses them for descending).
const sortByMagnitude = constructSortFn({ sort: numericSortComparator });
const sortByTimestamp = constructSortFn({ sort: timestampSortComparator });
const sortByMarketStatus = constructSortFn({
    sort: (v1, v2) => (MARKET_STATUS_SORT_RANK[v1] ?? 6) - (MARKET_STATUS_SORT_RANK[v2] ?? 6),
});

function areSortingsEqual(left, right) {
    return left.length === right.length && left.every((entry, index) => (
        entry.id === right[index].id && entry.desc === right[index].desc
    ));
}

function areColumnVisibilitiesEqual(left, right) {
    const normalizedLeft = left && typeof left === 'object' ? left : {};
    const normalizedRight = right && typeof right === 'object' ? right : {};
    const leftKeys = Object.keys(normalizedLeft);
    const rightKeys = Object.keys(normalizedRight);
    if (leftKeys.length !== rightKeys.length) {
        return false;
    }
    return leftKeys.every((key) => normalizedLeft[key] === normalizedRight[key]);
}

function formatIdentifierType(raw) {
    const normalized = raw == null ? '' : String(raw).trim();
    return normalized ? normalized.toUpperCase() : '';
}

function getUploadedIdentifierForRow(row = {}) {
    const inboundValue = row?.inbound_id_value == null ? '' : String(row.inbound_id_value).trim();
    return {
        type: inboundValue ? formatIdentifierType(row?.inbound_id_type) : '',
        value: inboundValue,
    };
}

/**
 * Format a decimal value as a percentage string with two decimal places.
 * Input is assumed to be a raw number or string (e.g., 0.15 => "15.00%").
 */
function percentFormatter(raw) {
    if (raw == null) return '';
    const val = parseFloat(raw);
    if (Number.isNaN(val)) return '';
    return (val * 100).toFixed(2) + '%';
}

function integerWithCommasFormatter(raw) {
    if (raw == null) return '';
    const num = Number(raw);
    if (Number.isNaN(num)) return '';
    const rounded = Math.round(num);
    return rounded.toLocaleString();
}

function twoDecimalFormatter(raw) {
    if (raw == null) return '';
    const val = parseFloat(raw);
    if (Number.isNaN(val)) return '';
    return val.toFixed(2);
}

const SOFT_FILLED_CHIP_SX = { opacity: 0.86 };
// The grid's status chips: the toolbar's soft chips (MUI's small chip in the
// app theme: 9px extra-bold capitals, 24px tall in either density, 8px
// beside the label) on the kit Chip.
const STATUS_CHIP_CLASS_NAME = '!text-[9px] !font-extrabold opacity-[0.86] [[data-compact]_&]:!h-6 '
    + '[&>span:last-child]:!px-2';
// MUI's disabled text colour, for values still loading.
const DISABLED_TEXT_CLASS_NAME = 'text-black/[0.38] dark:text-white/50';

// A status chip in the `tone` of the kit Chip, explained by `tooltip` on hover.
function renderStatusChip(tone, label, tooltip = null) {
    const chip = <KitChip tone={tone} label={label} className={STATUS_CHIP_CLASS_NAME} />;
    return tooltip ? <KitTooltip content={tooltip}>{chip}</KitTooltip> : chip;
}

// For controls whose clicks and keys are their own.
function stopPropagation(event) {
    event.stopPropagation();
}

// A value whose details show on hover.
function renderValueWithTooltip(value, tooltip) {
    return (
        <KitTooltip content={tooltip}>
            <span className="cursor-help">{value}</span>
        </KitTooltip>
    );
}

function formatQuoteClockTime(raw) {
    const parsed = parseTimestamp(raw);
    if (!parsed) return null;
    return parsed.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
    });
}

function buildPendingMarketDataLabel({ pendingCount, retryingCount }) {
    const totalCount = pendingCount + retryingCount;
    if (totalCount <= 0) return null;
    const noun = `securit${totalCount === 1 ? 'y' : 'ies'}`;
    if (pendingCount > 0 && retryingCount > 0) {
        return `Updating market data for ${totalCount} ${noun}`;
    }
    if (retryingCount > 0) {
        return `Retrying market data for ${retryingCount} ${noun}`;
    }
    return `Fetching market data for ${pendingCount} ${noun}`;
}

function WatchlistQuoteStatusIndicator({
    pendingCount = 0,
    retryingCount = 0,
    liveQuotesLastUpdatedAt = null,
    hasRows = false,
}) {
    if (!hasRows) {
        return null;
    }

    const pendingLabel = buildPendingMarketDataLabel({ pendingCount, retryingCount });
    if (pendingLabel) {
        return (
            <Tooltip
                title="Market data is still being fetched for some securities. This watchlist will update automatically."
                arrow
            >
                <Chip
                    size="small"
                    color="info"
                    label={pendingLabel}
                    sx={SOFT_FILLED_CHIP_SX}
                />
            </Tooltip>
        );
    }

    const clockLabel = formatQuoteClockTime(liveQuotesLastUpdatedAt);
    if (clockLabel) {
        return null;
    }

    return (
        <Tooltip
            title="Waiting for market-data status from the price loader."
            arrow
        >
            <Chip
                size="small"
                color="info"
                label="Waiting for market data"
                sx={SOFT_FILLED_CHIP_SX}
            />
        </Tooltip>
    );
}

function toFiniteNumber(raw) {
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
}

function parseTimestamp(raw) {
    if (!raw) return null;
    const parsed = raw instanceof Date ? raw : new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizePriceSource(raw) {
    const value = raw == null ? '' : String(raw).trim().toLowerCase();
    if (value === 'intraday') return 'intraday';
    if (value === 'daily_close') return 'daily_close';
    return null;
}

function normalizeMarketStatus(raw) {
    const value = raw == null ? '' : String(raw).trim().toLowerCase();
    if (value === 'open') return 'open';
    if (value === 'closed') return 'closed';
    return null;
}

function parseSessionDate(raw) {
    if (!raw) return null;
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
        return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
    }

    const text = String(raw).trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function formatSessionDate(raw, { includeYear = false } = {}) {
    const parsed = parseSessionDate(raw);
    if (!parsed) return null;
    return parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(includeYear ? { year: 'numeric' } : {}),
    });
}

function formatTimestampDisplay(raw, { includeYear = false } = {}) {
    const parsed = parseTimestamp(raw);
    if (!parsed) return '';

    const now = new Date();
    const isToday = (
        parsed.getFullYear() === now.getFullYear() &&
        parsed.getMonth() === now.getMonth() &&
        parsed.getDate() === now.getDate()
    );

    if (isToday) {
        return parsed.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
        });
    }

    return parsed.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(includeYear ? { year: 'numeric' } : {}),
        hour: 'numeric',
        minute: '2-digit',
    });
}

function formatLocalPrice(rawPrice, rawCurrency) {
    if (rawPrice == null) return '';
    const price = toFiniteNumber(rawPrice);
    const currency = rawCurrency == null ? '' : String(rawCurrency).trim();
    if (price === null) return '';

    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(price);
    return currency ? `${formatted} ${currency}` : formatted;
}

function renderPendingMarketDataValueCell(row) {
    return (
        <KitTooltip content={getWatchlistMarketStatusTooltip(row) || 'Waiting for market data to finish.'}>
            <span
                role="status"
                aria-label="Waiting for market data"
                className={cx('inline-flex min-h-6 w-full items-center justify-center', DISABLED_TEXT_CLASS_NAME)}
            >
                <Spinner size={12} thickness={5} />
            </span>
        </KitTooltip>
    );
}

function formatPriceTooltip(row) {
    const source = normalizePriceSource(row?._displayPriceSource ?? row?.price_source);
    const asOfLabel = formatSessionDate(
        row?._displayPriceAsOfDate ?? row?.price_asof_date,
        { includeYear: true },
    );
    const quoteLabel = formatTimestampDisplay(
        row?._displayQuoteTimestamp ?? row?.quote_timestamp ?? row?.updated_at,
        { includeYear: true },
    );
    const ingestedLabel = formatTimestampDisplay(
        row?._displayIngestedAt ?? row?.updated_at,
        { includeYear: true },
    );
    const localLabel = formatLocalPrice(row?.local_price, row?.local_currency);
    const fxRate = toFiniteNumber(row?.fx_rate);

    const details = [];
    if (source === 'intraday') {
        details.push(asOfLabel ? `Intraday quote for ${asOfLabel}` : 'Intraday quote');
    } else if (source === 'daily_close') {
        details.push(asOfLabel ? `Official close for ${asOfLabel}` : 'Official close');
    } else if (quoteLabel || ingestedLabel) {
        details.push('Stored price');
    }

    if (quoteLabel) {
        details.push(`Quoted ${quoteLabel}`);
    }
    if (localLabel && row?.local_currency && row?.local_currency !== 'USD') {
        details.push(`Local ${localLabel}`);
    }
    if (fxRate !== null && row?.local_currency && row?.local_currency !== 'USD') {
        details.push(`FX ${fxRate.toFixed(4)}`);
    }
    if (ingestedLabel) {
        details.push(`Ingested ${ingestedLabel}`);
    }

    return details.join(' • ') || 'Price update time not recorded.';
}

function deriveDisplayQuoteState(row, liveQuote) {
    const baseSource = normalizePriceSource(row?.price_source);
    const liveSource = normalizePriceSource(liveQuote?.price_source);
    const baseQuoteTimestamp = parseTimestamp(row?.quote_timestamp ?? row?.updated_at);
    const liveQuoteTimestamp = parseTimestamp(liveQuote?.quote_timestamp ?? liveQuote?.updated_at);
    const baseUpdatedAt = parseTimestamp(row?.updated_at);
    const liveUpdatedAt = parseTimestamp(liveQuote?.updated_at);
    const basePrice = toFiniteNumber(row?.price);
    const livePrice = toFiniteNumber(liveQuote?.price);
    const baseMarketStatus = normalizeMarketStatus(row?.market_status);
    const liveMarketStatus = normalizeMarketStatus(liveQuote?.market_status);
    const liveHasDifferentPrice = (
        livePrice !== null &&
        (basePrice === null || livePrice !== basePrice)
    );
    const liveIsNewer = (
        liveUpdatedAt &&
        baseUpdatedAt
    )
        ? liveUpdatedAt.getTime() > baseUpdatedAt.getTime()
        : Boolean(liveUpdatedAt && !baseUpdatedAt) || liveHasDifferentPrice;
    const liveIsOlder = liveUpdatedAt && baseUpdatedAt
        ? liveUpdatedAt.getTime() < baseUpdatedAt.getTime()
        : Boolean(liveQuoteTimestamp && baseQuoteTimestamp
            && liveQuoteTimestamp.getTime() < baseQuoteTimestamp.getTime());

    const preferLive = !liveIsOlder && (
        liveIsNewer ||
        (liveSource && !baseSource) ||
        (liveQuoteTimestamp && !baseQuoteTimestamp)
    );
    // An explicit null is a current price invalidation, such as failed quote
    // unit verification. Missing fields in partial responses can still use
    // the other snapshot; authoritative nulls must not restore an older price.
    const selectPriceField = (field) => {
        if (!liveIsOlder && liveQuote?.[field] === null) return null;
        const preferred = preferLive ? liveQuote : row;
        const alternate = preferLive ? row : liveQuote;
        return preferred?.[field] !== undefined
            ? preferred[field]
            : (alternate?.[field] ?? null);
    };
    const baseMarketAsOf = parseTimestamp(row?.market_status_asof);
    const liveMarketAsOf = parseTimestamp(liveQuote?.market_status_asof);
    // Exchange sessions change without a quote write. Use their own clock,
    // including a newer null when cached calendar coverage has expired.
    const preferLiveMarket = Object.prototype.hasOwnProperty.call(liveQuote || {}, 'market_status')
        && (liveMarketAsOf
            ? (!baseMarketAsOf || liveMarketAsOf.getTime() >= baseMarketAsOf.getTime())
            : (!baseMarketAsOf && (preferLive || !baseMarketStatus)));

    return {
        preferLive,
        displayPrice: selectPriceField('price'),
        displaySource: preferLive
            ? (liveSource ?? baseSource)
            : (baseSource ?? liveSource),
        displayPriceAsOfDate: preferLive
            ? (liveQuote?.price_asof_date ?? row?.price_asof_date ?? null)
            : (row?.price_asof_date ?? liveQuote?.price_asof_date ?? null),
        displayQuoteTimestamp: preferLive
            ? (liveQuoteTimestamp ?? baseQuoteTimestamp ?? liveUpdatedAt ?? baseUpdatedAt ?? null)
            : (baseQuoteTimestamp ?? liveQuoteTimestamp ?? baseUpdatedAt ?? liveUpdatedAt ?? null),
        displayIngestedAt: preferLive
            ? (liveUpdatedAt ?? baseUpdatedAt ?? null)
            : (baseUpdatedAt ?? liveUpdatedAt ?? null),
        displayLocalPrice: selectPriceField('local_price'),
        displayLocalCurrency: preferLive
            ? (liveQuote?.local_currency ?? row?.local_currency ?? null)
            : (row?.local_currency ?? liveQuote?.local_currency ?? null),
        displayFxRate: preferLive
            ? (liveQuote?.fx_rate ?? row?.fx_rate ?? null)
            : (row?.fx_rate ?? liveQuote?.fx_rate ?? null),
        marketStatus: preferLiveMarket ? liveMarketStatus : baseMarketStatus,
        marketStatusAsOf: preferLiveMarket
            ? (liveQuote?.market_status_asof ?? null)
            : (row?.market_status_asof ?? null),
    };
}

function renderMarketMetricValue(row, metric, value) {
    if (!value) return value;
    const explanation = [marketMetricExplanation(row, metric), marketDataUpdateTooltip(row?.[`${metric}_updated_at`])].filter(Boolean).join(' ');
    const qualifier = marketMetricQualifier(row, metric);
    // Focusable, so the explanation also shows (and describes it) on focus.
    return <KitTooltip content={explanation}>
        <span tabIndex={0} className="inline-flex cursor-help items-baseline gap-1">
            <span>{value}</span>
            {qualifier && <span className="text-[0.65rem] text-content-secondary">{qualifier}</span>}
        </span>
    </KitTooltip>;
}

function isTerminalWatchlistExclusion(row) {
    const readiness = String(row?.app_readiness_status || '').trim().toLowerCase();
    if (readiness === 'identity_unresolved' || readiness === 'market_registration_failed') {
        return true;
    }
    const marketRegistration = String(row?.market_registration_status || '').trim().toLowerCase();
    if (marketRegistration === 'failed') {
        return true;
    }
    return isWatchlistMarketDataUnavailable(row);
}

function isWatchlistReplacementPending(row) {
    return String(row?.market_registration_status || '').trim().toLowerCase() === 'replacing'
        && String(row?.app_readiness_status || '').trim().toLowerCase() === 'waiting_market_registration';
}

function isWatchlistMarketRegistrationPending(row) {
    return String(row?.app_readiness_status || '').trim().toLowerCase()
        === 'waiting_market_registration';
}

// The grid sorts with TanStack sorting ([{ id, desc }]); a user's saved sort
// keeps the format it has always been stored in, [{ field, sort }], so
// existing preferences (and their renamed fields) still apply.
export function readPersistedSorting(storageKey) {
    if (typeof window === 'undefined' || !storageKey) {
        return [];
    }

    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return [];

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        return parsed
            .filter((item) => item && typeof item.field === 'string')
            .map((item) => {
                const rawField = item.field;
                if (REMOVED_WATCHLIST_IDENTITY_SORT_FIELDS.has(rawField)) {
                    return null;
                }
                const field = (
                    rawField === 'price_updated_at_display' ||
                    rawField === 'price_updated_at_sort' ||
                    rawField === 'price_quote_at_display'
                )
                    ? 'price_quote_at_sort'
                    : rawField;
                return {
                    field,
                    sort: item.sort === 'desc' ? 'desc' : item.sort === 'asc' ? 'asc' : null,
                };
            })
            .filter(Boolean)
            .filter((item) => item.sort)
            .slice(0, 1)
            .map((item) => ({ id: item.field, desc: item.sort === 'desc' }));
    } catch (error) {
        console.warn('Failed to restore watchlist sort model:', error);
        return [];
    }
}

function serializePersistedSorting(sorting) {
    return JSON.stringify(sorting.map((entry) => ({
        field: entry.id,
        sort: entry.desc ? 'desc' : 'asc',
    })));
}

function normalizeCogniveIdList(values = []) {
    const normalized = [];
    const seen = new Set();

    values.forEach((value) => {
        const cogniveId = value == null ? '' : String(value).trim();
        if (!cogniveId || seen.has(cogniveId)) return;
        seen.add(cogniveId);
        normalized.push(cogniveId);
    });

    return normalized;
}

const buildAttemptId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `watchlist-remove-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const STALE_ADDITIONS_MESSAGE = 'One or more additions are out of date. Remove them and add the securities again, then save.';

// The draft history once attribute drafts, or the picks, are saved or gone.
const withoutAttributeSteps = (history) => filterDraftHistory(history, isPickHistoryAction);
const withoutPickSteps = (history) => filterDraftHistory(history, (action) => !isPickHistoryAction(action));

function reviewFirstMessage({ removals, additions }) {
    let changes = 'removals';
    if (additions) changes = removals ? 'removals and additions' : 'additions';
    return `Review ${changes} first. Your other edits are still unsaved.`;
}

export default function WatchlistTable({
    active = true,
    user,
    rows,
    portfolioEditor = null,
    portfolioDialogOpen = false,
    onClosePortfolioDialog = null,
    watchlistAttributeSnapshot = null,
    loading,
    replacingWatchlist = false,
    liveQuotesByCogniveId = {},
    liveQuotesLastUpdatedAt = null,
    imputedCogniveIds = [],
    ignoredCogniveIds = [],
    factorModelLabel = '',
    betaSource = 'yahoo',
    factorContextUnavailable = false,
    factorMatricesUnavailable = false,
    uploadControl = null,
    m365SyncControl = null,
    reviewHandoff = null,
    onReviewHandoff = null,
    watchlistContextSync = null,
    showUploadGuide = false,
    onDismissGuide,
    securityOperationStatus = null,
    publicationRefreshBusy = false,
    publicationNeedsAttention = false,
    newsByIdentity = {},
    newsMoverThresholdPct = null,
    newsLastUpdatedAt = null,
    newsRecencyWindowHours = 24,
    newsReadArticleIds = null,
    onNewsArticleRead = null,
    deepDiveFocusAreas = null,
    onLoadNewsDetails = null,
    newsArticlesByIdentity = null,
    newsArticlesLoadingByIdentity = null,
    newsArticlesErrorByIdentity = null,
}) {
    const { sessionScopeToken } = useSecurityOperationContext();
    const deletionOwnerKey = JSON.stringify([user?.uid || '', sessionScopeToken || '']);
    const deletionOwnerRef = useRef(deletionOwnerKey);
    deletionOwnerRef.current = deletionOwnerKey;
    const [toolbarActionsEl, setToolbarActionsEl] = useState(null);
    const incomingRows = useMemo(
        () => (Array.isArray(rows) ? rows : []),
        [rows],
    );
    const activeOperationStatus = securityOperationStatus;
    const activeOperationOverall = activeOperationStatus?.found
        ? deriveSecurityOperationOverallStatus(
            activeOperationStatus.phases,
            activeOperationStatus.topLevelStatus,
        )
        : null;
    const operationOwnsWatchlistSnapshot = (
        activeOperationOverall === OVERALL_STATUS_RUNNING
        && shouldShowSecurityOperationBanner(activeOperationStatus)
        && String(activeOperationStatus?.operationType || '').trim() === 'watchlist_update'
    );
    // The previous client snapshot remains the sole renderable table until
    // the parent operation and the browser's required first-paint refresh
    // have both completed. In particular, releasing the local refresh flag
    // while the shared progress document is still at 99% must not expose a
    // partially assembled replacement.
    const publicationSnapshotLocked = Boolean(
        replacingWatchlist
        || publicationRefreshBusy
        || publicationNeedsAttention
        || operationOwnsWatchlistSnapshot
    );
    const settledRowsRef = useRef(incomingRows);
    const settledAttributeSnapshotRef = useRef(watchlistAttributeSnapshot);
    const settledNewsByIdentityRef = useRef(newsByIdentity || {});
    const settledLiveQuotesByCogniveIdRef = useRef(liveQuotesByCogniveId || {});
    if (!publicationSnapshotLocked) {
        settledRowsRef.current = incomingRows;
        settledAttributeSnapshotRef.current = watchlistAttributeSnapshot;
        settledNewsByIdentityRef.current = newsByIdentity || {};
        settledLiveQuotesByCogniveIdRef.current = liveQuotesByCogniveId || {};
    }
    // The backend's active-watchlist pointer moves atomically, but the
    // dashboard refresh contains several independent requests. Keep painting
    // the previous, complete client snapshot until all of those requests have
    // settled, then swap rows and news together in one render.
    const effectiveRows = publicationSnapshotLocked
        ? settledRowsRef.current
        : incomingRows;
    const effectiveAttributeSnapshot = publicationSnapshotLocked
        ? settledAttributeSnapshotRef.current
        : watchlistAttributeSnapshot;
    const effectiveNewsByIdentity = publicationSnapshotLocked
        ? settledNewsByIdentityRef.current
        : (newsByIdentity || {});
    const effectiveLiveQuotesByCogniveId = publicationSnapshotLocked
        ? settledLiveQuotesByCogniveIdRef.current
        : (liveQuotesByCogniveId || {});
    const activePinnedIdentityFields = useMemo(() => getWatchlistPinnedIdentityFields(), []);

    const [watchlistResolutionSummary, setWatchlistResolutionSummary] = useState(null);
    const [identityDisplayField, setIdentityDisplayField] = useState('company_name');
    // One basis (currency and dividends) for all four return columns,
    // remembered by this browser.
    const [returnsBasis, setReturnsBasis] = useState(readPersistedReturnsBasis);
    const [returnsHeaderAnchorEl, setReturnsHeaderAnchorEl] = useState(null);
    const changeReturnsBasis = (basis) => {
        setReturnsBasis(basis);
        persistReturnsBasis(basis);
    };

    // By default, hide the "Return 1m" column
    const [columnVisibility, setColumnVisibility] = useState({
        'Return 1m': false,
    });
    // The first visible return column holds the basis menu.
    const returnsControlColumnId = TRAILING_RETURN_WINDOWS
        .map((returnWindow) => returnWindow.columnId)
        .find((columnId) => columnVisibility[columnId] !== false) ?? null;
    const sortStorageKey = `${WATCHLIST_SORT_STORAGE_KEY_PREFIX}:${user?.uid || 'anonymous'}`;
    const [sortPreference, setSortPreference] = useState(() => ({
        storageKey: sortStorageKey,
        sorting: readPersistedSorting(sortStorageKey),
    }));
    // Authentication can settle after this component mounts. Resolve the new
    // user's persisted sort synchronously for that render; the effect below
    // only adopts the already-visible value into local interaction state.
    const sorting = useMemo(() => (
        sortPreference.storageKey === sortStorageKey
            ? sortPreference.sorting
            : readPersistedSorting(sortStorageKey)
    ), [sortPreference, sortStorageKey]);
    const [flashBySedol, setFlashBySedol] = useState({});
    const [overrideFeedback, setOverrideFeedback] = useState({ open: false, message: '', severity: 'error' });
    const [historyOpen, setHistoryOpen] = useState(false);
    const [pendingGuidance, setPendingGuidance] = useState(null);
    const [historyButtonEl, setHistoryButtonEl] = useState(null);
    useEffect(() => {
        setPendingGuidance(null);
        const receive = (event) => {
            if (event.detail?.sessionScopeToken === sessionScopeToken) setPendingGuidance(event.detail);
        };
        window.addEventListener(WATCHLIST_DRAFT_GUIDANCE_EVENT, receive);
        return () => window.removeEventListener(WATCHLIST_DRAFT_GUIDANCE_EVENT, receive);
    }, [sessionScopeToken]);
    const [historyRevisionRequest, setHistoryRevisionRequest] = useState(null);
    const historyRequestCounterRef = useRef(0);
    const lastOptimizerReviewCommandRef = useRef(null);
    const previousLivePricesRef = useRef({});
    const flashTimeoutsRef = useRef({});
    const [portfolioDraftByToken, setPortfolioDraftByToken] = useState({});
    const [portfolioDraftBase, setPortfolioDraftBase] = useState(null);
    const [portfolioDraftStale, setPortfolioDraftStale] = useState(false);
    const [portfolioSaving, setPortfolioSaving] = useState(false);
    const [portfolioSaveError, setPortfolioSaveError] = useState('');
    const [portfolioQuantityEditor, setPortfolioQuantityEditor] = useState({
        anchorEl: null,
        row: null,
        value: '',
        validationVisible: false,
    });
    const portfolioAttemptIdRef = useRef(null);
    const portfolioSaveInFlightRef = useRef(false);
    const [watchlistFieldEditor, setWatchlistFieldEditor] = useState({
        loading: false,
        error: '',
        attributeSnapshot: null,
        rowsByCogniveId: {},
        scopeKey: '',
        userUid: '',
    });
    const [addSecurityOpen, setAddSecurityOpen] = useState(false);
    const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
    const [quickFilterQuery, setQuickFilterQuery] = useState('');
    // A new key remounts the quick filter, so it opens with a query set here.
    const [quickFilterKey, setQuickFilterKey] = useState(0);
    // Attribute column filters for this session: { [definitionId]: filter }.
    const [categoryFilters, setCategoryFilters] = useState({});
    const [categoryLimitNoticeOpen, setCategoryLimitNoticeOpen] = useState(false);
    const gridCommandsRef = useRef(null);
    const [genericAttributeDrafts, setGenericAttributeDrafts] = useState({});
    // Securities picked in the grid's "+ Add security" row, not saved yet.
    const [picks, setPicks] = useState(EMPTY_PICKS);
    const [additionErrorMessage, setAdditionErrorMessage] = useState('');
    // One Stage attempt id per addition payload, kept until it stages, so a
    // retry after an uncertain failure repeats the same attempt.
    const additionAttemptsRef = useRef(new Map());
    // Undo/redo, one entry per user action: attribute drafts and changes of
    // the picks, in the order they were made.
    const [draftHistory, setDraftHistory] = useState(EMPTY_DRAFT_HISTORY);
    const [genericAttributeSaving, setGenericAttributeSaving] = useState(false);
    const [attributeManagerOpen, setAttributeManagerOpen] = useState(false);
    const [attributeAiOpen, setAttributeAiOpen] = useState(false);
    const [attributeAiRecipe, setAttributeAiRecipe] = useState(null);
    const [genericAttributeError, setGenericAttributeError] = useState('');
    const genericAttributeAttemptIdRef = useRef(null);
    const watchlistSaveInFlightRef = useRef(false);
    const [watchlistSaveInFlight, setWatchlistSaveInFlight] = useState(false);
    const [deletionDraftByToken, setDeletionDraftByToken] = useState({});
    const [deletionSaving, setDeletionSaving] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
    const deleteAttemptRef = useRef(null);
    useEffect(() => {
        const currentDeleteTokens = new Set(
            incomingRows
                .filter((row) => row?.delete_group_token)
                .map((row) => String(row.delete_group_token)),
        );
        setDeletionDraftByToken((current) => {
            if (Object.keys(current).length === 0) return current;
            const next = Object.fromEntries(
                Object.entries(current).filter(([token]) => currentDeleteTokens.has(token)),
            );
            if (Object.keys(current).length === Object.keys(next).length) return current;
            deleteAttemptRef.current = null;
            return next;
        });
    }, [incomingRows]);
    const watchlistResolutionRefreshKey = useMemo(() => {
        const sourceRows = effectiveRows;
        const uploadRunIds = Array.from(new Set(
            sourceRows
                .map((row) => (row?.upload_run_id == null ? '' : String(row.upload_run_id).trim()))
                .filter(Boolean),
        )).sort();
        return `${sourceRows.length}:${uploadRunIds.join(',')}`;
    }, [effectiveRows]);
    const watchlistOperationRevisionKey = String(
        activeOperationStatus?.watchlist_revision_id
        || activeOperationStatus?.revisionId
        || activeOperationStatus?.revision_id
        || '',
    ).trim();
    const watchlistFieldEditorScopeKey = [
        user?.uid || '',
        watchlistResolutionRefreshKey,
        watchlistOperationRevisionKey,
    ].join(':');
    const watchlistFieldEditorRequestIdRef = useRef(0);

    const refreshWatchlistFieldEditor = useCallback(async () => {
        const requestId = watchlistFieldEditorRequestIdRef.current + 1;
        watchlistFieldEditorRequestIdRef.current = requestId;
        const requestedScopeKey = watchlistFieldEditorScopeKey;
        const requestedUserUid = String(user?.uid || '');
        if (!user?.uid) {
            setWatchlistFieldEditor({
                loading: false,
                error: '',
                attributeSnapshot: null,
                rowsByCogniveId: {},
                scopeKey: requestedScopeKey,
                userUid: '',
            });
            return;
        }
        setWatchlistFieldEditor((current) => ({ ...current, loading: true, error: '' }));
        let response;
        try {
            response = await fetchWatchlistFieldEditor();
        } catch (error) {
            if (requestId !== watchlistFieldEditorRequestIdRef.current) return;
            setWatchlistFieldEditor((current) => ({
                ...current,
                loading: false,
                error: parseCloudRunError(error).message
                    || 'Watchlist fields are temporarily unavailable.',
                scopeKey: requestedScopeKey,
            }));
            return;
        }
        if (requestId !== watchlistFieldEditorRequestIdRef.current) return;
        if (response?.success === false) {
            setWatchlistFieldEditor((current) => ({
                ...current,
                loading: false,
                error: parseCloudRunError(response?.message || response).message
                    || 'Watchlist fields are temporarily unavailable.',
                scopeKey: requestedScopeKey,
            }));
            return;
        }
        const rowsByCogniveId = Object.fromEntries(
            (Array.isArray(response?.rows) ? response.rows : [])
                .map((row) => [String(row?.cognive_id || '').trim(), row])
                .filter(([cogniveId]) => Boolean(cogniveId)),
        );
        const attributeSnapshot = response?.watchlist_attribute_snapshot || null;
        setWatchlistFieldEditor({
            loading: false,
            error: '',
            attributeSnapshot,
            rowsByCogniveId,
            scopeKey: requestedScopeKey,
            userUid: requestedUserUid,
        });
        const validDefinitionIds = new Set(
            (Array.isArray(attributeSnapshot?.definitions)
                ? attributeSnapshot.definitions
                : []
            ).map((definition) => String(
                definition?.attribute_definition_id || '',
            ).trim()).filter(Boolean),
        );
        setGenericAttributeDrafts((current) => {
            const next = Object.fromEntries(Object.entries(current).filter(([, draft]) => {
                const row = rowsByCogniveId[draft.cogniveId];
                if (!row || !validDefinitionIds.has(draft.attributeDefinitionId)) return false;
                const baselineValue = watchlistFieldText(
                    row?.watchlist_attribute_values?.[draft.attributeDefinitionId],
                ).trim();
                return draft.value !== baselineValue;
            }));
            if (Object.keys(next).length === 0) {
                genericAttributeAttemptIdRef.current = null;
            }
            return next;
        });
        // Attribute entries refer to the replaced snapshot and to drafts the
        // refresh may have dropped; the picks' entries stay.
        setDraftHistory(withoutAttributeSteps);
    }, [user?.uid, watchlistFieldEditorScopeKey]);

    useEffect(() => {
        if (publicationSnapshotLocked) {
            // The Dashboard publication transaction is the only owner allowed
            // to advance visible Watchlist data. Invalidate any older editor
            // request and keep its complete metadata snapshot until that
            // transaction commits the replacement rows.
            watchlistFieldEditorRequestIdRef.current += 1;
            return;
        }
        void refreshWatchlistFieldEditor();
    }, [
        publicationSnapshotLocked,
        refreshWatchlistFieldEditor,
        watchlistOperationRevisionKey,
        watchlistResolutionRefreshKey,
    ]);

    const watchlistFieldEditorReady = (
        !publicationSnapshotLocked
        && watchlistFieldEditor.scopeKey === watchlistFieldEditorScopeKey
        && !watchlistFieldEditor.loading
    );
    const watchlistColumnSchemaReady = (
        !user?.uid
        || (
            publicationSnapshotLocked
                ? Boolean(watchlistFieldEditor.scopeKey)
                : watchlistFieldEditorReady
        )
    );
    const isInitialLoading = Boolean(
        effectiveRows.length === 0 && (loading || !watchlistColumnSchemaReady)
    );

    const genericAttributeDefinitions = useMemo(() => {
        const currentUserUid = String(user?.uid || '');
        const displaySnapshot = effectiveAttributeSnapshot
            || watchlistFieldEditor.attributeSnapshot;
        if (!effectiveAttributeSnapshot && watchlistFieldEditor.userUid !== currentUserUid) {
            return [];
        }
        const definitions = displaySnapshot?.definitions;
        return Array.isArray(definitions)
            ? definitions.filter(
                (definition) => (
                    !isSystemCountryDefinition(definition)
                    && isWatchlistGroupingAttribute(definition)
                ),
            ).sort(
                (left, right) => Number(left.display_order) - Number(right.display_order),
            )
            : [];
    }, [effectiveAttributeSnapshot, user?.uid, watchlistFieldEditor]);
    const { preferences: ownGroupPreferences, changePreference: changeOwnGroupPreference } = useWatchlistOwnGroupPreferences(
        user?.uid || '', genericAttributeDefinitions,
    );
    const [ownGroupState, setOwnGroupState] = useState(null);

    const imputedCogniveIdSet = useMemo(
        () => new Set(normalizeCogniveIdList(imputedCogniveIds)),
        [imputedCogniveIds],
    );
    const ignoredCogniveIdSet = useMemo(
        () => new Set(normalizeCogniveIdList(ignoredCogniveIds)),
        [ignoredCogniveIds],
    );
    const portfolioDirty = Object.keys(portfolioDraftByToken).length > 0;
    const portfolioStateHash = portfolioEditor?.portfolio_state_hash ?? null;
    const portfolioSnapshotFingerprint = portfolioEditor?.editor_snapshot_fingerprint ?? null;

    const clearPortfolioDraft = useCallback(() => {
        setPortfolioDraftByToken({});
        setPortfolioSaveError('');
        setPortfolioDraftBase(null);
        setPortfolioDraftStale(false);
        setPortfolioQuantityEditor({
            anchorEl: null,
            row: null,
            value: '',
            validationVisible: false,
        });
        portfolioAttemptIdRef.current = null;
    }, []);

    useEffect(() => {
        clearPortfolioDraft();
        setDeletionDraftByToken({});
        setDeleteErrorMessage('');
        deleteAttemptRef.current = null;
        // Picks carry the previous user's signed tokens.
        setPicks(EMPTY_PICKS);
        setDraftHistory(withoutPickSteps);
        setAdditionErrorMessage('');
        additionAttemptsRef.current.clear();
        setDeletionSaving(false);
        setWatchlistSaveInFlight(false);
        watchlistSaveInFlightRef.current = false;
        const owner = deletionOwnerKey;
        deletionOwnerRef.current = owner;
        return () => {
            if (deletionOwnerRef.current === owner) deletionOwnerRef.current = null;
        };
    }, [clearPortfolioDraft, deletionOwnerKey]);

    useEffect(() => {
        if (!portfolioDirty || !portfolioDraftBase) return;
        if (!portfolioStateHash || !portfolioSnapshotFingerprint) return;
        if (
            portfolioDraftBase.portfolioStateHash !== portfolioStateHash
            || portfolioDraftBase.snapshotFingerprint !== portfolioSnapshotFingerprint
        ) {
            setPortfolioDraftStale(true);
        }
    }, [
        portfolioDirty,
        portfolioDraftBase,
        portfolioSnapshotFingerprint,
        portfolioStateHash,
    ]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleApplied = () => clearPortfolioDraft();
        window.addEventListener('portfolio-import-applied', handleApplied);
        return () => window.removeEventListener('portfolio-import-applied', handleApplied);
    }, [clearPortfolioDraft]);

    useEffect(() => {
        let isMounted = true;
        if (!user?.uid) {
            setWatchlistResolutionSummary(null);
            return () => {
                isMounted = false;
            };
        }

        fetchWatchlistResolutionSummary()
            .then((summary) => {
                if (isMounted) {
                    setWatchlistResolutionSummary(summary || null);
                }
            })
            .catch((error) => {
                console.warn('Failed to load watchlist resolution summary:', error);
                if (isMounted) {
                    setWatchlistResolutionSummary(null);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [user?.uid, watchlistResolutionRefreshKey]);

    useEffect(() => {
        if (sortPreference.storageKey === sortStorageKey) return;
        setSortPreference({
            storageKey: sortStorageKey,
            sorting: readPersistedSorting(sortStorageKey),
        });
    }, [sortPreference.storageKey, sortStorageKey]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (sortPreference.storageKey !== sortStorageKey) return;

        try {
            if (sortPreference.sorting.length) {
                window.localStorage.setItem(
                    sortStorageKey,
                    serializePersistedSorting(sortPreference.sorting),
                );
            } else {
                window.localStorage.removeItem(sortStorageKey);
            }
        } catch (error) {
            console.warn('Failed to persist watchlist sort model:', error);
        }
    }, [sortPreference, sortStorageKey]);

    useEffect(() => {
        const nextFlashes = {};
        const previousPrices = previousLivePricesRef.current;

        Object.entries(effectiveLiveQuotesByCogniveId).forEach(([cogniveId, quote]) => {
            const nextPrice = Number(quote?.price);
            if (!Number.isFinite(nextPrice)) {
                return;
            }

            const prevPrice = previousPrices[cogniveId];
            if (Number.isFinite(prevPrice) && nextPrice !== prevPrice) {
                nextFlashes[cogniveId] = nextPrice > prevPrice ? 'up' : 'down';
            }
            previousPrices[cogniveId] = nextPrice;
        });

        if (!Object.keys(nextFlashes).length) {
            return;
        }

        setFlashBySedol((current) => ({ ...current, ...nextFlashes }));
        Object.entries(nextFlashes).forEach(([cogniveId]) => {
            if (flashTimeoutsRef.current[cogniveId]) {
                clearTimeout(flashTimeoutsRef.current[cogniveId]);
            }
            flashTimeoutsRef.current[cogniveId] = window.setTimeout(() => {
                setFlashBySedol((current) => {
                    const next = { ...current };
                    delete next[cogniveId];
                    return next;
                });
                delete flashTimeoutsRef.current[cogniveId];
            }, 3200);
        });
    }, [effectiveLiveQuotesByCogniveId]);

    useEffect(() => {
        return () => {
            Object.values(flashTimeoutsRef.current).forEach((timeoutId) => {
                clearTimeout(timeoutId);
            });
        };
    }, []);

    const showOverrideFeedback = useCallback((message, severity = 'error') => {
        setOverrideFeedback({
            open: true,
            message,
            severity,
        });
    }, []);

    const openHistoryPanel = useCallback((target = {}) => {
        const revisionId = target?.revisionId == null ? '' : String(target.revisionId).trim();
        historyRequestCounterRef.current += 1;
        setHistoryRevisionRequest(revisionId ? {
            requestId: historyRequestCounterRef.current,
            revisionId,
            revisionSeq: target?.revisionSeq ?? null,
        } : null);
        setPendingGuidance(null);
        setHistoryOpen(true);
    }, []);

    useEffect(() => {
        const commandId = reviewHandoff?.commandId;
        if (
            !active
            || !['optimizer_restoration', 'watchlist_removal', 'watchlist_addition', 'watchlist_context_refresh', 'watchlist_context_status'].includes(reviewHandoff?.origin)
            || !Number.isInteger(commandId)
            || lastOptimizerReviewCommandRef.current === commandId
            || !sessionScopeToken
            || reviewHandoff.sessionScopeToken !== sessionScopeToken
        ) return;
        lastOptimizerReviewCommandRef.current = commandId;
        openHistoryPanel(reviewHandoff.origin === 'watchlist_context_refresh'
            ? {
                revisionId: reviewHandoff.revisionId,
                revisionSeq: reviewHandoff.revisionSeq ?? null,
            }
            : {});
    }, [active, openHistoryPanel, reviewHandoff, sessionScopeToken]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handler = (event) => {
            const detail = event?.detail || {};
            if (!isSecurityOperationEventForScope(detail, sessionScopeToken)) return;
            openHistoryPanel({
                revisionId: detail.revisionId || detail.watchlist_revision_id,
                revisionSeq: detail.revisionSeq || detail.revision_seq,
            });
        };
        window.addEventListener(WATCHLIST_HISTORY_OPEN_EVENT, handler);
        return () => {
            window.removeEventListener(WATCHLIST_HISTORY_OPEN_EVENT, handler);
        };
    }, [openHistoryPanel, sessionScopeToken]);

    const { pending: transferredImport, transfer } = useImportFileTransfer();
    useEffect(() => {
        if (!transferredImport) return;
        if (active && transferredImport.destination === 'watchlist') openHistoryPanel({});
        if (transferredImport.destination === 'portfolio') setHistoryOpen(false);
    }, [active, transferredImport, openHistoryPanel]);
    // A pasted sheet is a Watchlist import, like an uploaded file: the same
    // column check, review and Apply (see pastedSheet.js).
    const importPastedSheet = (pastedRows) => {
        if (!transfer || !isPastedSheet(pastedRows, 'watchlist')) return false;
        transfer('watchlist', pastedSheetFile(pastedRows));
        return true;
    };
    // A paste elsewhere on the Watchlist page; the grid's own Mod+V reaches
    // importPastedSheet through interceptPaste instead.
    const watchlistPageRef = usePagePaste({
        enabled: active,
        onPaste: (text) => {
            if (importPastedSheet(parseTsv(text))) return;
            if (gridCommandsRef.current?.paste(text)) return;
            showOverrideFeedback(PAGE_PASTE_HINT, 'info');
        },
    });

    const rowData = useMemo(() => effectiveRows.map((row, index) => {
        if (!row) {
            return { id: `empty-${index}` };
        }

        const cogniveId = row?.cognive_id == null ? '' : String(row.cognive_id).trim();
        const quoteKey = cogniveId;
        const liveQuote = quoteKey ? effectiveLiveQuotesByCogniveId[quoteKey] : null;
        const newsItem = cogniveId && effectiveNewsByIdentity
            ? effectiveNewsByIdentity[cogniveId]
            : null;
        const newsArticleCount = Array.isArray(newsItem?.recent_articles)
            ? newsItem.recent_articles.length
            : 0;
        const securityDetailArticles = cogniveId && newsArticlesByIdentity
            ? newsArticlesByIdentity[cogniveId] || null
            : null;
        const securityDetailLoading = Boolean(
            cogniveId
            && newsArticlesLoadingByIdentity
            && newsArticlesLoadingByIdentity[cogniveId]
        );
        const securityDetailError = cogniveId && newsArticlesErrorByIdentity
            ? newsArticlesErrorByIdentity[cogniveId] || null
            : null;
        const missingFactorStatus = cogniveId && imputedCogniveIdSet.has(cogniveId)
            ? 'imputed'
            : cogniveId && ignoredCogniveIdSet.has(cogniveId)
                ? 'ignored'
                : null;
        const {
            preferLive,
            displayPrice,
            displaySource,
            displayPriceAsOfDate,
            displayQuoteTimestamp,
            displayIngestedAt,
            displayLocalPrice,
            displayLocalCurrency,
            displayFxRate,
            marketStatus,
            marketStatusAsOf,
        } = deriveDisplayQuoteState(row, liveQuote);
        const hasPriceData = displayPrice != null && toFiniteNumber(displayPrice) !== null;
        const uploadedIdentifier = getUploadedIdentifierForRow(row);

        return {
            id: row.portfolio_selection_token || cogniveId || index,
            ...row,
            display_identifier_type: uploadedIdentifier.type,
            display_identifier: uploadedIdentifier.value,
            _missingFactorStatus: missingFactorStatus,
            _missingPriceData: !hasPriceData,
            _isImputedFactorData: missingFactorStatus === 'imputed',
            price: displayPrice,
            local_price: displayLocalPrice,
            local_currency: displayLocalCurrency,
            ...(betaSource === 'yahoo' ? selectMarketMetric(row, liveQuote, 'beta', preferLive) : {}),
            ...selectMarketMetric(row, liveQuote, 'adtv', preferLive),
            price_source: displaySource ?? row.price_source ?? null,
            price_asof_date: displayPriceAsOfDate,
            quote_timestamp: displayQuoteTimestamp ? displayQuoteTimestamp.toISOString() : null,
            updated_at: displayIngestedAt ? displayIngestedAt.toISOString() : null,
            market_status: marketStatus,
            market_status_asof: marketStatusAsOf,
            market_health: latestMarketHealth(row.market_health, liveQuote?.market_health),
            fx_rate: displayFxRate,
            fx_source: preferLive
                ? (liveQuote?.fx_source ?? row.fx_source ?? null)
                : (row.fx_source ?? liveQuote?.fx_source ?? null),
            fx_timestamp: preferLive
                ? (liveQuote?.fx_timestamp ?? row.fx_timestamp ?? null)
                : (row.fx_timestamp ?? liveQuote?.fx_timestamp ?? null),
            next_earnings_date:
                liveQuote?.next_earnings_date ?? row.next_earnings_date ?? null,
            next_earnings_observed_at:
                liveQuote?.next_earnings_observed_at ?? row.next_earnings_observed_at ?? null,
            yf_symbol: liveQuote?.yf_symbol ?? row.yf_symbol ?? null,
            yf_exchange: liveQuote?.yf_exchange ?? row.yf_exchange ?? null,
            yf_exch_disp: liveQuote?.yf_exch_disp ?? row.yf_exch_disp ?? null,
            yf_shortname: liveQuote?.yf_shortname ?? row.yf_shortname ?? null,
            yf_longname: liveQuote?.yf_longname ?? row.yf_longname ?? null,
            yahoo_cache_last_checked:
                liveQuote?.yahoo_cache_last_checked ?? row.yahoo_cache_last_checked ?? null,
            article_count: liveQuote?.article_count ?? row.article_count ?? newsArticleCount,
            article_as_of: liveQuote?.article_as_of ?? row.article_as_of ?? null,
            security_updated_at:
                liveQuote?.security_updated_at ?? row.security_updated_at ?? null,
            price_quote_at_display: formatTimestampDisplay(displayQuoteTimestamp),
            price_quote_at_full: formatTimestampDisplay(displayQuoteTimestamp, { includeYear: true }),
            price_quote_at_sort: displayQuoteTimestamp ? displayQuoteTimestamp.getTime() : null,
            market_data_status: liveQuote?.market_data_status ?? row.market_data_status ?? null,
            market_data_status_code:
                liveQuote?.market_data_status_code
                ?? row.market_data_status_code
                ?? null,
            actual_portfolio_market_data_status:
                liveQuote?.actual_portfolio_market_data_status
                ?? row.actual_portfolio_market_data_status
                ?? null,
            pos_sizing_market_data_status:
                liveQuote?.pos_sizing_market_data_status
                ?? row.pos_sizing_market_data_status
                ?? null,
            actual_portfolio_ready:
                liveQuote?.actual_portfolio_ready
                ?? row.actual_portfolio_ready
                ?? null,
            pos_sizing_ready:
                liveQuote?.pos_sizing_ready
                ?? row.pos_sizing_ready
                ?? null,
            _displayPriceSource: displaySource,
            _displayPriceAsOfDate: displayPriceAsOfDate,
            _displayQuoteTimestamp: displayQuoteTimestamp,
            _displayIngestedAt: displayIngestedAt,
            _priceFlashDirection: quoteKey ? flashBySedol[quoteKey] ?? null : null,
            _newsItem: newsItem,
            _newsArticleCount: newsArticleCount,
            _securityDetailArticles: securityDetailArticles,
            _securityDetailLoading: securityDetailLoading,
            _securityDetailError: securityDetailError,
        };
    }).filter((row) => !isTerminalWatchlistExclusion(row)), [
        effectiveRows,
        effectiveLiveQuotesByCogniveId,
        flashBySedol,
        betaSource,
        imputedCogniveIdSet,
        ignoredCogniveIdSet,
        effectiveNewsByIdentity,
        newsArticlesByIdentity,
        newsArticlesLoadingByIdentity,
        newsArticlesErrorByIdentity,
    ]);

    const portfolioStateForRow = useCallback((row) => {
        const token = row?.portfolio_selection_token;
        const draft = token ? portfolioDraftByToken[token] : null;
        const authoritativeChecked = Boolean(row?.in_actual_portfolio);
        const checked = draft ? Boolean(draft.checked) : authoritativeChecked;
        const quantity = draft?.checked
            ? (draft.quantity ?? '')
            : authoritativeChecked
                ? (row?.actual_quantity ?? '')
                : '';
        return { checked, quantity, authoritativeChecked, draft };
    }, [portfolioDraftByToken]);

    const beginPortfolioDraft = useCallback(() => {
        if (watchlistSaveInFlight) return;
        setPortfolioDraftBase((current) => current || {
            portfolioStateHash,
            snapshotFingerprint: portfolioSnapshotFingerprint,
        });
    }, [portfolioSnapshotFingerprint, portfolioStateHash, watchlistSaveInFlight]);

    const handlePortfolioMembershipChange = useCallback((row, checked) => {
        if (watchlistSaveInFlight) return;
        const token = row?.portfolio_selection_token;
        if (!token || !row?.portfolio_editability?.editable) return;
        beginPortfolioDraft();
        setPortfolioDraftByToken((current) => {
            const authoritativeChecked = Boolean(row?.in_actual_portfolio);
            const next = { ...current };
            if (Boolean(checked) === authoritativeChecked) {
                delete next[token];
                return next;
            }
            next[token] = {
                checked: Boolean(checked),
                quantity: checked ? (current[token]?.quantity ?? '') : '',
            };
            return next;
        });
    }, [beginPortfolioDraft, watchlistSaveInFlight]);

    const closePortfolioQuantityEditor = useCallback(() => {
        setPortfolioQuantityEditor({
            anchorEl: null,
            row: null,
            value: '',
            validationVisible: false,
        });
    }, []);

    const openPortfolioQuantityEditor = useCallback((row, anchorEl) => {
        if (watchlistSaveInFlight) return;
        if (!row?.portfolio_selection_token || !row?.portfolio_editability?.editable) return;
        const state = portfolioStateForRow(row);
        setPortfolioQuantityEditor({
            anchorEl,
            row,
            value: state.checked ? String(state.quantity ?? '') : '',
            validationVisible: false,
        });
    }, [portfolioStateForRow, watchlistSaveInFlight]);

    const handlePortfolioQuantityChange = useCallback((row, quantity) => {
        if (watchlistSaveInFlight || portfolioSaving) return;
        const token = row?.portfolio_selection_token;
        if (!token || !row?.portfolio_editability?.editable) return;
        beginPortfolioDraft();
        setPortfolioDraftByToken((current) => {
            const next = { ...current };
            if (row.in_actual_portfolio && quantity === String(row.actual_quantity ?? '').trim()) {
                delete next[token];
            } else {
                next[token] = { checked: true, quantity };
            }
            return next;
        });
    }, [beginPortfolioDraft, portfolioSaving, watchlistSaveInFlight]);

    const applyPortfolioQuantityEditor = useCallback(() => {
        if (watchlistSaveInFlight) {
            closePortfolioQuantityEditor();
            return;
        }
        const row = portfolioQuantityEditor.row;
        const token = row?.portfolio_selection_token;
        const quantity = String(portfolioQuantityEditor.value ?? '').trim();
        if (!token || !row?.portfolio_editability?.editable) {
            closePortfolioQuantityEditor();
            return;
        }
        if (!isValidPortfolioQuantity(quantity)) {
            setPortfolioQuantityEditor((current) => ({
                ...current,
                validationVisible: true,
            }));
            return;
        }

        handlePortfolioQuantityChange(row, quantity);
        closePortfolioQuantityEditor();
    }, [
        handlePortfolioQuantityChange,
        closePortfolioQuantityEditor,
        portfolioQuantityEditor,
        watchlistSaveInFlight,
    ]);

    useEffect(() => {
        if (portfolioDirty) return;
        setPortfolioDraftBase(null);
        setPortfolioDraftStale(false);
        portfolioAttemptIdRef.current = null;
    }, [portfolioDirty]);

    const invalidPortfolioRows = useMemo(() => rowData.filter((row) => {
        const state = portfolioStateForRow(row);
        return state.checked && !isValidPortfolioQuantity(state.quantity);
    }), [portfolioStateForRow, rowData]);

    const authoritativePortfolioRows = useMemo(
        () => (Array.isArray(rows) ? rows : []).filter((row) => row?.portfolio_selection_token),
        [rows],
    );

    const savePortfolioDraft = useCallback(async () => {
        const remainingDraftTokens = new Set(
            authoritativePortfolioRows
                .filter((row) => (
                    portfolioDraftByToken[row?.portfolio_selection_token]
                ))
                .map((row) => row.portfolio_selection_token),
        );
        if (!remainingDraftTokens.size) {
            clearPortfolioDraft();
            return true;
        }
        const effectiveBaseStateHash = portfolioDraftBase?.portfolioStateHash
            || portfolioStateHash;
        if (
            !portfolioDirty
            || portfolioDraftStale
            || invalidPortfolioRows.length > 0
            || portfolioSaveInFlightRef.current
            || !effectiveBaseStateHash
        ) return false;

        const positions = authoritativePortfolioRows.flatMap((row) => {
            const state = portfolioStateForRow(row);
            if (!state.checked || !row?.portfolio_selection_token) return [];
            return [{
                portfolio_selection_token: row.portfolio_selection_token,
                quantity: String(state.quantity).trim(),
            }];
        });

        portfolioSaveInFlightRef.current = true;
        setPortfolioSaving(true);
        setPortfolioSaveError('');
        try {
            const requestKey = JSON.stringify({
                baseStateHash: effectiveBaseStateHash,
                positions: [...positions].sort((left, right) => (
                    left.portfolio_selection_token.localeCompare(
                        right.portfolio_selection_token,
                    )
                )),
            });
            if (portfolioAttemptIdRef.current?.requestKey !== requestKey) {
                portfolioAttemptIdRef.current = {
                    requestKey,
                    attemptId: createPortfolioAttemptId(),
                };
            }
            const response = await stageWatchlistPortfolioEditor({
                attempt_id: portfolioAttemptIdRef.current.attemptId,
                base_state_hash: effectiveBaseStateHash,
                positions,
            });
            clearPortfolioDraft();
            if (response?.status === 'pending_apply' || response?.import_run) {
                window.dispatchEvent(new CustomEvent('imports-updated'));
                window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_MODAL_OPEN_EVENT, {
                    detail: {
                        mode: 'review',
                        importRun: response?.import_run || null,
                    },
                }));
                showOverrideFeedback('Portfolio changes are ready for review.', 'success');
            } else {
                showOverrideFeedback('The portfolio already matches these selections.', 'info');
            }
            return true;
        } catch (error) {
            const parsed = parseCloudRunError(error);
            if (parsed.statusCode === 409 && parsed.code?.includes('stale')) {
                setPortfolioDraftStale(true);
            }
            if (parsed.statusCode === 409 && parsed.detail?.existing_import_run_id) {
                window.dispatchEvent(new CustomEvent('imports-updated'));
                window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_MODAL_OPEN_EVENT, {
                    detail: {
                        mode: 'review',
                        importRun: null,
                        importRunId: parsed.detail.existing_import_run_id,
                    },
                }));
            }
            showOverrideFeedback(parsed.message || 'Failed to stage portfolio changes.', 'error');
            setPortfolioSaveError(parsed.message || 'Failed to stage portfolio changes.');
            return false;
        } finally {
            portfolioSaveInFlightRef.current = false;
            setPortfolioSaving(false);
        }
    }, [
        clearPortfolioDraft,
        authoritativePortfolioRows,
        invalidPortfolioRows,
        portfolioDirty,
        portfolioDraftBase,
        portfolioDraftByToken,
        portfolioDraftStale,
        portfolioStateForRow,
        portfolioStateHash,
        showOverrideFeedback,
    ]);

    const marketStatusSummary = useMemo(() => {
        let openCount = 0;
        let closedCount = 0;

        rowData.forEach((row) => {
            if (row?.market_status === 'open') {
                openCount += 1;
            } else if (row?.market_status === 'closed') {
                closedCount += 1;
            }
        });

        return {
            openCount,
            closedCount,
            totalCount: rowData.length,
        };
    }, [rowData]);

    const imputedCount = useMemo(
        () => rowData.filter((row) => row?._missingFactorStatus === 'imputed').length,
        [rowData],
    );
    const ignoredCount = useMemo(
        () => rowData.filter((row) => row?._missingFactorStatus === 'ignored').length,
        [rowData],
    );
    const noPriceCount = useMemo(
        () => rowData.filter((row) => row?._missingPriceData).length,
        [rowData],
    );
    const pendingMarketDataCount = useMemo(
        () => rowData.filter((row) => isWatchlistMarketDataPending(row)).length,
        [rowData],
    );
    const retryingMarketDataCount = useMemo(
        () => rowData.filter((row) => isWatchlistMarketDataRetrying(row)).length,
        [rowData],
    );
    const unresolvedWatchlistCount = useMemo(() => {
        if (replacingWatchlist) return 0;
        const count = Number(watchlistResolutionSummary?.unresolved_count);
        return Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
    }, [replacingWatchlist, watchlistResolutionSummary]);
    const factorStatusTooltipTitle = useMemo(() => {
        const detailParts = [];
        if (factorContextUnavailable) {
            detailParts.push(
                'This Watchlist needs context validation before current factor and risk data can load. Open Watchlist History to prepare the validation; an unsuccessful validation leaves the published Watchlist unchanged.',
            );
        } else if (factorMatricesUnavailable) {
            detailParts.push(
                `Factor data is unavailable for ${factorModelLabel || 'the active model'} in the current matrix load. Watchlist rows remain visible; risk metrics will populate when coverage becomes available.`,
            );
        }
        if (imputedCount > 0) {
            detailParts.push(
                `${imputedCount} securit${imputedCount === 1 ? 'y is' : 'ies are'} recorded with unsupported factor estimates for ${factorModelLabel || 'the active model'}; verified factor data is required.`,
            );
        }
        if (ignoredCount > 0) {
            detailParts.push(
                `${ignoredCount} securit${ignoredCount === 1 ? 'y is' : 'ies are'} currently missing factor data and ignored for ${factorModelLabel || 'the active model'}.`,
            );
        }
        if (noPriceCount > 0) {
            detailParts.push(
                `${noPriceCount} securit${noPriceCount === 1 ? 'y has' : 'ies have'} no stored price yet and will update automatically if market-data coverage becomes available.`,
            );
        }
        if (!detailParts.length) {
            return '';
        }
        detailParts.push(
            'Highlighted rows and status badges reflect the latest factor-matrix load.',
        );
        return detailParts.join(' ');
    }, [factorContextUnavailable, factorMatricesUnavailable, factorModelLabel, ignoredCount, imputedCount, noPriceCount]);

    const deletionDirty = Object.keys(deletionDraftByToken).length > 0;
    const additionsDirty = picks.length > 0;
    // Out-of-date picks block Save until they are removed or picked again.
    const stalePicks = hasStalePicks(picks);
    const stagedDeletionTokens = useMemo(
        () => new Set(Object.keys(deletionDraftByToken)),
        [deletionDraftByToken],
    );
    const stagedDeletionCogniveIds = useMemo(
        () => new Set(
            incomingRows
                .filter((row) => stagedDeletionTokens.has(
                    String(row?.delete_group_token || ''),
                ))
                .map((row) => String(row?.cognive_id || '').trim())
                .filter(Boolean),
        ),
        [incomingRows, stagedDeletionTokens],
    );
    const retainedPortfolioDraftDirty = useMemo(
        () => authoritativePortfolioRows.some((row) => (
            !stagedDeletionTokens.has(String(row?.delete_group_token || ''))
            && Boolean(portfolioDraftByToken[row?.portfolio_selection_token])
        )),
        [authoritativePortfolioRows, portfolioDraftByToken, stagedDeletionTokens],
    );
    const invalidRetainedPortfolioRows = useMemo(
        () => invalidPortfolioRows.filter(
            (row) => !stagedDeletionTokens.has(String(row?.delete_group_token || '')),
        ),
        [invalidPortfolioRows, stagedDeletionTokens],
    );

    const getRowClassName = useCallback((row) => {
        const classNames = [];
        if (stagedDeletionTokens.has(String(row?.delete_group_token || ''))) {
            classNames.push('watchlist-staged-delete-row');
        }
        if (row?._missingFactorStatus === 'imputed') {
            classNames.push('watchlist-imputed-row');
        }
        if (row?._missingFactorStatus === 'ignored') {
            classNames.push('watchlist-ignored-row');
        }
        if (marketHealthDistressed(row?.market_health)) {
            classNames.push('watchlist-distressed-row');
        }
        return classNames.join(' ');
    }, [stagedDeletionTokens]);

    const genericAttributeDraftKey = useCallback((cogniveId, definitionId) => (
        `${cogniveId}\u0000${definitionId}`
    ), []);
    const displayedAttributeValuesByCogniveId = useMemo(() => {
        const valuesByCognive = {};
        (Array.isArray(effectiveAttributeSnapshot?.values)
            ? effectiveAttributeSnapshot.values
            : []
        ).forEach((item) => {
            const cogniveId = String(item?.cognive_id || '').trim();
            const definitionId = String(item?.attribute_definition_id || '').trim();
            if (!cogniveId || !definitionId) return;
            if (!valuesByCognive[cogniveId]) valuesByCognive[cogniveId] = {};
            valuesByCognive[cogniveId][definitionId] = item?.value_text ?? '';
        });
        return valuesByCognive;
    }, [effectiveAttributeSnapshot]);
    const genericAttributeBaseline = useCallback((row, definitionId) => {
        const cogniveId = String(row?.cognive_id || '').trim();
        if (effectiveAttributeSnapshot) {
            return watchlistFieldText(
                displayedAttributeValuesByCogniveId[cogniveId]?.[definitionId] ?? '',
            );
        }
        return watchlistFieldText(
            watchlistFieldEditor.rowsByCogniveId[cogniveId]
                ?.watchlist_attribute_values?.[definitionId],
        );
    }, [
        displayedAttributeValuesByCogniveId,
        effectiveAttributeSnapshot,
        watchlistFieldEditor.rowsByCogniveId,
    ]);
    const genericAttributeValue = useCallback((row, definitionId) => {
        const cogniveId = String(row?.cognive_id || '').trim();
        const key = genericAttributeDraftKey(cogniveId, definitionId);
        return Object.prototype.hasOwnProperty.call(genericAttributeDrafts, key)
            ? genericAttributeDrafts[key].value
            : genericAttributeBaseline(row, definitionId);
    }, [
        genericAttributeBaseline,
        genericAttributeDraftKey,
        genericAttributeDrafts,
    ]);
    // Several cell edits as one draft change and one undo step:
    // edits = [{ row, definition, value }]. Values are trimmed text, and a
    // value equal to the saved one removes that cell's draft. Returns the
    // recorded history action, or null when nothing changed.
    const applyGenericAttributeDraftBatch = useCallback((edits, label) => {
        if (watchlistSaveInFlight || !watchlistFieldEditorReady) return null;
        const normalizedEdits = [];
        edits.forEach(({ row, definition, value }) => {
            const cogniveId = String(row?.cognive_id || '').trim();
            const definitionId = String(definition?.attribute_definition_id || '').trim();
            if (!cogniveId || !definitionId) return;
            normalizedEdits.push({
                key: genericAttributeDraftKey(cogniveId, definitionId),
                cogniveId,
                attributeDefinitionId: definitionId,
                value: watchlistFieldText(value).trim(),
                baselineValue: genericAttributeBaseline(row, definitionId).trim(),
            });
        });
        const action = buildDraftAction(genericAttributeDrafts, normalizedEdits, label);
        if (!action) return null;
        setGenericAttributeDrafts((current) => applyDraftChanges(current, action.changes, 'after'));
        setDraftHistory((current) => recordDraftAction(current, action));
        genericAttributeAttemptIdRef.current = null;
        return action;
    }, [
        genericAttributeBaseline,
        genericAttributeDraftKey,
        genericAttributeDrafts,
        watchlistSaveInFlight,
        watchlistFieldEditorReady,
    ]);
    const updateGenericAttributeDraft = useCallback((row, definition, value, label = 'Edit') => (
        applyGenericAttributeDraftBatch([{ row, definition, value }], label)
    ), [applyGenericAttributeDraftBatch]);
    // A change of the picks (from the add row), one undo step.
    const applyPicksAction = useCallback((action) => {
        setPicks(action.picks.after);
        setDraftHistory((current) => recordDraftAction(current, action));
        setAdditionErrorMessage('');
    }, []);
    // Undo and Redo take the newest step of either kind; attribute steps
    // wait while the attribute editor is not ready, and nothing moves while
    // a save is in flight.
    const canStepDraftHistory = (action) => Boolean(action) && !watchlistSaveInFlight
        && (isPickHistoryAction(action) || watchlistFieldEditorReady);
    const stepDraftHistory = useCallback((takeStep, side, expectedAction = null) => {
        const step = takeStep(draftHistory);
        if (!step || (expectedAction && step.action !== expectedAction)) return null;
        if (watchlistSaveInFlight) return null;
        if (isPickHistoryAction(step.action)) {
            setPicks(step.action.picks[side]);
            setAdditionErrorMessage('');
        } else {
            if (!watchlistFieldEditorReady) return null;
            setGenericAttributeDrafts((current) => applyDraftChanges(current, step.action.changes, side));
            genericAttributeAttemptIdRef.current = null;
        }
        setDraftHistory(step.history);
        return step.action;
    }, [draftHistory, watchlistFieldEditorReady, watchlistSaveInFlight]);
    // `expectedAction` (from an Undo toast) only undoes that exact action.
    const undoDraftStep = useCallback((expectedAction = null) => (
        stepDraftHistory(takeUndo, 'before', expectedAction)
    ), [stepDraftHistory]);
    const redoDraftStep = useCallback(() => (
        stepDraftHistory(takeRedo, 'after')
    ), [stepDraftHistory]);
    // The spreadsheet layer and the category tools edit attribute cells of
    // real rows through the drafts above; Save, Discard and publication are
    // unchanged.
    const genericAttributeEditingEnabled = watchlistFieldEditorReady && !watchlistSaveInFlight;
    const rowDataById = useMemo(
        () => new Map(rowData.map((row) => [String(row.id), row])),
        [rowData],
    );
    const attributeDefinitionsByField = useMemo(() => new Map(
        genericAttributeDefinitions.map((definition) => [
            `${WATCHLIST_ATTRIBUTE_FIELD_PREFIX}${definition.attribute_definition_id}`,
            definition,
        ]),
    ), [genericAttributeDefinitions]);
    const isGenericAttributeCellEditable = (rowId, field) => {
        const definition = attributeDefinitionsByField.get(field);
        const row = rowDataById.get(rowId);
        return Boolean(
            genericAttributeEditingEnabled
            && definition
            && definition.editable !== false
            && row
            && String(row.cognive_id || '').trim()
        );
    };
    // The quick filter and the column filters narrow only what the table
    // shows; saving, the download and the counts above keep every Watchlist
    // row. Both match saved attribute values, so a row does not vanish while
    // one of its cells is being edited.
    const quickFilteredRows = useMemo(() => filterWatchlistRows(
        rowData,
        quickFilterQuery,
        (row) => genericAttributeDefinitions.map((definition) => (
            genericAttributeBaseline(row, definition.attribute_definition_id)
        )),
    ), [genericAttributeBaseline, genericAttributeDefinitions, quickFilterQuery, rowData]);
    const categoryColumns = useMemo(() => genericAttributeDefinitions.map((definition) => ({
        key: definition.attribute_definition_id,
        field: `${WATCHLIST_ATTRIBUTE_FIELD_PREFIX}${definition.attribute_definition_id}`,
        label: definition.display_name,
        editable: definition.editable !== false && genericAttributeEditingEnabled,
    })), [genericAttributeDefinitions, genericAttributeEditingEnabled]);
    const categoryTools = useCategoryColumnTools({
        columns: categoryColumns,
        filters: categoryFilters,
        onFiltersChange: setCategoryFilters,
        rows: rowData,
        scopeRows: quickFilteredRows,
        getRowId: watchlistGridRowId,
        readSavedValue: genericAttributeBaseline,
        readValue: genericAttributeValue,
        isEditable: isGenericAttributeCellEditable,
        applyEdits: (edits, label, describe) => (
            gridCommandsRef.current?.applyEdits(edits, label, describe) ?? null
        ),
    });
    const categoryLimitBlocked = categoryTools.limitViolations.length > 0;
    const genericAttributesDirty = Object.keys(genericAttributeDrafts).length > 0;
    const retainedGenericAttributesDirty = Object.values(genericAttributeDrafts).some(
        (draft) => !stagedDeletionCogniveIds.has(draft.cogniveId),
    );
    const ownGroupSnapshot = effectiveAttributeSnapshot || watchlistFieldEditor.attributeSnapshot;
    const ownGroupCogniveIds = useMemo(() => normalizeCogniveIdList(
        effectiveRows.map((row) => row?.cognive_id),
    ).sort(), [effectiveRows]);
    const ownGroupAttributeIds = useMemo(() => Object.keys(ownGroupPreferences).sort(), [ownGroupPreferences]);
    const ownGroupSourceKey = 'yahoo_daily_total_return_snapshots';
    // The displayed risk projection includes derived attributes such as Country;
    // its fingerprints differ from the raw editing snapshot for the same revision.
    const ownGroupSnapshotPending = effectiveAttributeSnapshot && watchlistFieldEditor.attributeSnapshot
        && effectiveAttributeSnapshot.revision_id !== watchlistFieldEditor.attributeSnapshot.revision_id;
    const ownGroupBlockedReason = genericAttributesDirty || deletionDirty || additionsDirty
        ? 'Save or discard Watchlist category and membership changes before using own-group statistics.'
        : publicationSnapshotLocked || !watchlistFieldEditorReady || ownGroupSnapshotPending
            ? 'Waiting for the accepted Watchlist revision.' : '';
    // The query fences metric selections separately; keep the editor mounted as choices change.
    const ownGroupViewKey = JSON.stringify([user?.uid || '', sessionScopeToken, ownGroupSnapshot?.revision_id,
        ownGroupSnapshot?.definition_fingerprint, ownGroupSnapshot?.content_fingerprint,
        ownGroupCogniveIds, ownGroupSourceKey, ownGroupBlockedReason]);
    const visibleOwnGroupState = ownGroupState?.viewKey === ownGroupViewKey ? ownGroupState : null;
    const ownGroupColumns = useMemo(() => buildOwnGroupColumns(
        genericAttributeDefinitions, ownGroupPreferences, visibleOwnGroupState,
    ), [genericAttributeDefinitions, ownGroupPreferences, visibleOwnGroupState]);
    // Stated once above the table while a shown own-group column has no values.
    const ownGroupNotice = ownGroupColumns.some((column) => columnVisibility[column.id] !== false)
        ? visibleOwnGroupState?.notice : '';

    const handleAttributeMutationResult = useCallback(async (response) => {
        if (response?.success !== true) {
            throw new Error('Could not update Watchlist attributes.');
        }
        if (response.combined_draft) {
            showWatchlistDraftGuidance(response, sessionScopeToken);
            return { success: true, pendingReview: true, publicationDetail: null };
        }
        let publicationDetail = null;
        if (response.changed) {
            const lifecycle = normalizeWatchlistLifecycle(response);
            const receipt = parseWatchlistPublicationReceipt(response);
            if (lifecycle.status !== 'succeeded' || lifecycle.phase !== 'complete' || !receipt) {
                throw new Error('The Watchlist attribute update receipt is invalid. Retry the save.');
            }
            publicationDetail = scopeSecurityOperationEventDetail({
                lifecycle,
                origin: 'watchlist_table',
                reason: 'watchlist_generic_attribute_edit',
                watchlist_revision_id: receipt.revisionId,
                revision_seq: receipt.revisionSeq,
                ui_commit_complete: false,
            }, sessionScopeToken);
            publicationDetail = publicationDetail ? {
                ...publicationDetail,
                delta_classification: WATCHLIST_DELTA_CLASSIFICATIONS.CUSTOM_METADATA_CHANGED,
            } : null;
            if (!publicationDetail || !dispatchMsiSecurityResolutionAppliedEvent(publicationDetail, sessionScopeToken)) {
                throw new Error('The Watchlist session changed before publication. Retry the save.');
            }
        }
        await refreshWatchlistFieldEditor();
        return { success: true, publicationDetail };
    }, [refreshWatchlistFieldEditor, sessionScopeToken]);

    const applyGenericAttributeOperations = useCallback(async (
        operations,
        {
            clearValueDrafts = false,
        } = {},
    ) => {
        if (
            watchlistSaveInFlight
            || genericAttributeSaving
            || publicationSnapshotLocked
            || !watchlistFieldEditorReady
            || !Array.isArray(operations)
            || !operations.length
        ) return null;
        const expectedWatchlistRevisionId = String(
            watchlistFieldEditor.attributeSnapshot?.revision_id
            || '',
        ).trim();
        const expectedAttributeFingerprint = String(
            watchlistFieldEditor.attributeSnapshot?.definition_fingerprint
            || '',
        ).trim();
        if (!expectedWatchlistRevisionId || !expectedAttributeFingerprint) {
            setGenericAttributeError(
                'The current Watchlist attribute revision is unavailable. Refresh and try again.',
            );
            return null;
        }
        const requestKey = JSON.stringify({
            operations,
            expectedWatchlistRevisionId,
            expectedAttributeFingerprint,
        });
        if (genericAttributeAttemptIdRef.current?.requestKey !== requestKey) {
            genericAttributeAttemptIdRef.current = {
                requestKey,
                attemptId: createPortfolioAttemptId(),
            };
        }
        setGenericAttributeSaving(true);
        setGenericAttributeError('');
        try {
            const response = await applyWatchlistAttributeMutations({
                operations,
                attemptId: genericAttributeAttemptIdRef.current.attemptId,
                expectedWatchlistRevisionId,
                expectedAttributeFingerprint,
            });
            if (response?.success !== true) {
                throw new Error(
                    parseCloudRunError(response?.message || response).message
                    || 'Could not update Watchlist attributes.',
                );
            }
            const result = await handleAttributeMutationResult(response);
            if (clearValueDrafts) {
                setGenericAttributeDrafts({});
                setDraftHistory(withoutAttributeSteps);
            }
            genericAttributeAttemptIdRef.current = null;
            return result;
        } catch (error) {
            const parsed = parseCloudRunError(error);
            if (showWatchlistDraftGuidance(parsed, sessionScopeToken)) return null;
            setGenericAttributeError(
                parsed.message || 'Could not update Watchlist attributes.',
            );
            return null;
        } finally {
            setGenericAttributeSaving(false);
        }
    }, [
        genericAttributeSaving,
        handleAttributeMutationResult,
        publicationSnapshotLocked,
        refreshWatchlistFieldEditor,
        sessionScopeToken,
        watchlistFieldEditor.attributeSnapshot,
        watchlistFieldEditorReady,
        watchlistSaveInFlight,
    ]);

    const saveGenericAttributeDrafts = useCallback(async () => {
        const operations = Object.values(genericAttributeDrafts)
            .map((draft) => ({
                op: 'set_value',
                attribute_definition_id: draft.attributeDefinitionId,
                cognive_id: draft.cogniveId,
                value: draft.value,
            }));
        if (!operations.length) {
            setGenericAttributeDrafts({});
            setDraftHistory(withoutAttributeSteps);
            genericAttributeAttemptIdRef.current = null;
            return { success: true, publicationDetail: null };
        }
        return applyGenericAttributeOperations(operations, {
            clearValueDrafts: true,
        });
    }, [applyGenericAttributeOperations, genericAttributeDrafts]);

    const clearPendingWatchlistChanges = useCallback(() => {
        setGenericAttributeDrafts({});
        setPicks(EMPTY_PICKS);
        setDraftHistory(EMPTY_DRAFT_HISTORY);
        setCategoryLimitNoticeOpen(false);
        genericAttributeAttemptIdRef.current = null;
        setAdditionErrorMessage('');
        additionAttemptsRef.current.clear();
        setDeletionDraftByToken({});
        setDeleteErrorMessage('');
        deleteAttemptRef.current = null;
        clearPortfolioDraft();
    }, [clearPortfolioDraft]);

    const isOperationInProgress = Boolean(
        publicationSnapshotLocked
        || ['queued', 'running'].includes(activeOperationStatus?.topLevelStatus)
        || deletionSaving
        || watchlistSaveInFlight
    );

    const toggleDeletionDraft = useCallback((row) => {
        const token = String(row?.delete_group_token || '');
        if (!token || isOperationInProgress) return;
        setDeletionDraftByToken((current) => {
            const next = { ...current };
            if (next[token]) {
                delete next[token];
            } else {
                next[token] = {
                    deleteToken: token,
                    // Keep the displayed impact with the local selection.
                    // Staging assesses the current impact for the shared review;
                    // only that review's Apply approves the removal.
                    confirmedPortfolioRemoval: Boolean(
                        row?.delete_will_remove_portfolio,
                    ),
                };
            }
            return next;
        });
        deleteAttemptRef.current = null;
        setDeleteErrorMessage('');
    }, [isOperationInProgress]);

    // Stages the selected removals for the shared review; returns
    // { revisionId, revisionSeq }, or null with the reason shown.
    const stageDeletionDrafts = useCallback(async () => {
        const deletions = Object.values(deletionDraftByToken);
        if (!deletions.length || deletionSaving) return null;
        const owner = deletionOwnerRef.current;
        const requestKey = JSON.stringify(deletions.map((deletion) => [
            deletion.deleteToken, deletion.confirmedPortfolioRemoval,
        ]).sort((left, right) => left[0].localeCompare(right[0])));
        if (deleteAttemptRef.current?.requestKey !== requestKey) {
            deleteAttemptRef.current = { requestKey, attemptId: buildAttemptId() };
        }
        setDeletionSaving(true);
        setDeleteErrorMessage('');
        try {
            const response = await stageSecurityResolutionRemovals({
                deletions,
                attemptId: deleteAttemptRef.current.attemptId,
            });
            if (deletionOwnerRef.current !== owner) return null;
            const revisionId = String(response?.watchlist_revision_id || '').trim();
            if (response?.success !== true || response?.pending_review !== true || !revisionId
                || (response.status && !['pending_review', 'pending_apply'].includes(response.status))) {
                throw new Error(response?.message || 'The removal review is unavailable. Retry the same selection.');
            }
            setDeletionDraftByToken({});
            deleteAttemptRef.current = null;
            return { revisionId, revisionSeq: response.revision_seq };
        } catch (error) {
            if (deletionOwnerRef.current !== owner) return null;
            const parsed = parseCloudRunError(error);
            if (showWatchlistDraftGuidance(parsed, sessionScopeToken)) return null;
            setDeleteErrorMessage(parsed.message || 'Failed to prepare Watchlist removals for review.');
            return null;
        } finally {
            if (deletionOwnerRef.current === owner) setDeletionSaving(false);
        }
    }, [deletionDraftByToken, deletionSaving, sessionScopeToken]);

    // Stages the picks as additions, as the Add Security dialog does
    // (stagePicks). Staged picks leave the grid; the first failure is shown,
    // and out-of-date choices are marked, which blocks Save until they are
    // removed or picked again. Returns the last review staged
    // ({ revisionId, revisionSeq }) or null.
    const stagePickedAdditions = useCallback(async (owner) => {
        const outcome = await stagePicks(picks, {
            expectedWatchlistRevisionId: String(
                watchlistFieldEditor.attributeSnapshot?.revision_id || '',
            ).trim() || undefined,
            sessionScopeToken,
            attempts: additionAttemptsRef.current,
            newAttemptId: buildAttemptId,
            isCurrent: () => deletionOwnerRef.current === owner,
            onMarkedStale: (previous, marked) => {
                setDraftHistory((current) => markStalePicksInHistory(current, previous, marked));
            },
        });
        if (!outcome) return null;
        const { picks: remaining, review, failure, notice } = outcome;
        if (remaining !== picks) {
            setPicks(remaining);
            // Staged picks are in the review now; their steps cannot be undone here.
            if (remaining.length < picks.length) setDraftHistory(withoutPickSteps);
        }
        setAdditionErrorMessage(failure);
        if (notice) showOverrideFeedback(notice, 'info');
        return review;
    }, [picks, sessionScopeToken, showOverrideFeedback, watchlistFieldEditor.attributeSnapshot]);

    // Removals, then additions, staged into one pending Watchlist change and
    // handed once to the shared review, whose Apply approves them. The other
    // local edits wait for that decision.
    const stageMembershipChanges = useCallback(async (owner) => {
        if (!sessionScopeToken || typeof onReviewHandoff !== 'function') {
            const message = 'The Watchlist review is unavailable. Refresh and try again.';
            if (deletionDirty) setDeleteErrorMessage(message);
            else setAdditionErrorMessage(message);
            return null;
        }
        let staged = null;
        if (deletionDirty) {
            const removal = await stageDeletionDrafts();
            if (!removal || deletionOwnerRef.current !== owner) return null;
            staged = { origin: 'watchlist_removal', ...removal };
        }
        if (additionsDirty) {
            const addition = await stagePickedAdditions(owner);
            if (deletionOwnerRef.current !== owner) return null;
            if (addition) staged = { origin: 'watchlist_addition', ...addition };
        }
        if (!staged) return null;
        onReviewHandoff({
            ...staged,
            status: 'changes_awaiting_review',
            sessionScopeToken,
        });
        setPendingGuidance(null);
        return staged;
    }, [additionsDirty, deletionDirty, onReviewHandoff, sessionScopeToken, stageDeletionDrafts, stagePickedAdditions]);

    // Build Watchlist additions hands its staged change to the same review.
    const handOffDialogAdditions = useCallback((handoff) => {
        onReviewHandoff(handoff);
        setPendingGuidance(null);
    }, [onReviewHandoff]);

    const savePendingWatchlistChanges = useCallback(async () => {
        if (watchlistSaveInFlightRef.current) return;
        // The server refuses a column above the category limit; say which
        // one before sending anything. Removals and additions are staged
        // first and leave attribute edits unsaved, so they are not held back.
        const membershipDirty = deletionDirty || additionsDirty;
        const attributeSaveBlocked = !membershipDirty && genericAttributesDirty && categoryLimitBlocked;
        setCategoryLimitNoticeOpen(attributeSaveBlocked);
        if (attributeSaveBlocked) return;
        const owner = deletionOwnerRef.current;
        watchlistSaveInFlightRef.current = true;
        setWatchlistSaveInFlight(true);
        closePortfolioQuantityEditor();
        try {
            if (membershipDirty) {
                // Membership changes are only staged here. The shared review
                // owns approval; preserve unrelated local edits until that
                // decision is complete.
                const staged = await stageMembershipChanges(owner);
                if (staged && deletionOwnerRef.current === owner && (genericAttributesDirty || portfolioDirty)) {
                    showOverrideFeedback(reviewFirstMessage({
                        removals: deletionDirty,
                        additions: staged.origin === 'watchlist_addition',
                    }), 'info');
                }
                return;
            }
            if (genericAttributesDirty) {
                const result = await saveGenericAttributeDrafts();
                if (!result?.success || deletionOwnerRef.current !== owner) return;
            }
            if (portfolioDirty) await savePortfolioDraft();
        } finally {
            if (deletionOwnerRef.current === owner) {
                watchlistSaveInFlightRef.current = false;
                setWatchlistSaveInFlight(false);
            }
        }
    }, [additionsDirty, categoryLimitBlocked, closePortfolioQuantityEditor, deletionDirty, genericAttributesDirty,
        portfolioDirty, saveGenericAttributeDrafts, savePortfolioDraft, showOverrideFeedback, stageMembershipChanges]);

    // The grid selects attribute cells and opens their editor (click the
    // active cell, double-click, Enter, F2 or type); a cell only shows its
    // value, the draft tint and the per-cell undo.
    const renderGenericAttributeCell = useCallback((row, definition) => {
        const cogniveId = String(row?.cognive_id || '').trim();
        const definitionId = definition.attribute_definition_id;
        const key = genericAttributeDraftKey(cogniveId, definitionId);
        const value = genericAttributeValue(row, definitionId);
        const dirty = Object.prototype.hasOwnProperty.call(genericAttributeDrafts, key);
        const companyName = securityCompanyName(row);
        return (
            <div className="flex w-full items-center gap-0.5">
                <span
                    className={cx(
                        'flex min-w-0 flex-1 items-center overflow-hidden rounded-[5px] px-1 py-1 [line-height:normal]',
                        // An unsaved change: MUI's warning orange, faintly.
                        dirty && 'bg-[#ed6c02]/[0.14] dark:bg-[#ffa726]/[0.14]',
                    )}
                >
                    <CategoryPill value={value} />
                </span>
                {dirty && (
                    <KitTooltip content={`Undo ${definition.display_name} change`}>
                        <KitIconButton
                            label={`Undo ${definition.display_name} for ${companyName}`}
                            // Keep focus on the grid: this button goes away once used.
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => {
                                event.stopPropagation();
                                updateGenericAttributeDraft(
                                    row,
                                    definition,
                                    genericAttributeBaseline(row, definitionId),
                                    'Undo change',
                                );
                            }}
                            disabled={watchlistSaveInFlight}
                        >
                            <Undo2 aria-hidden="true" size={15} />
                        </KitIconButton>
                    </KitTooltip>
                )}
            </div>
        );
    }, [
        genericAttributeBaseline,
        genericAttributeDraftKey,
        genericAttributeDrafts,
        genericAttributeValue,
        updateGenericAttributeDraft,
        watchlistSaveInFlight,
    ]);

    const editableWatchlistAttributeColumns = useMemo(() => (
        genericAttributeDefinitions.map((definition) => ({
                id: `watchlist_attribute:${definition.attribute_definition_id}`,
                // The label with the column's category filter button.
                header: () => (
                    <CategoryColumnHeader
                        columnKey={definition.attribute_definition_id}
                        label={definition.display_name}
                    />
                ),
                size: 128,
                accessorFn: (row) => genericAttributeValue(
                    row,
                    definition.attribute_definition_id,
                ),
                sortFn: sortByText,
                cell: ({ row }) => renderGenericAttributeCell(row.original, definition),
                meta: {
                    label: definition.display_name,
                    headerClassName: watchlistColumnGroupClass('identity'),
                    cellClassName: joinClassNames(
                        watchlistColumnGroupClass('identity'),
                        definition.editable !== false && watchlistFieldEditorReady
                            ? 'user-input-col'
                            : '',
                    ),
                },
            }))
    ), [
        genericAttributeDefinitions,
        genericAttributeValue,
        renderGenericAttributeCell,
        watchlistFieldEditorReady,
    ]);

    const columns = useMemo(() => {
        return [
            {
                id: 'in_actual_portfolio',
                header: 'Portfolio',
                size: 78,
                enableCellSelection: false,
                meta: {
                    align: 'center',
                    headerAlign: 'center',
                    headerClassName: watchlistColumnGroupClass('identity'),
                    cellClassName: joinClassNames(
                        'portfolio-membership-cell',
                        watchlistColumnGroupClass('identity'),
                    ),
                },
                cell: ({ row: { original: row } }) => {
                    const state = portfolioStateForRow(row);
                    const editability = row?.portfolio_editability || {};
                    const disabled = (
                        !editability.editable
                        || portfolioSaving
                        || watchlistSaveInFlight
                    );
                    const securityLabel = row?.ticker || row?.display_identifier || 'security';
                    const pendingType = !state.draft
                        ? 'unchanged'
                        : !state.checked
                            ? 'pending-removal'
                            : state.authoritativeChecked
                                ? 'pending-edit'
                                : 'pending-addition';
                    const tooltipTitle = editability.message || (
                        pendingType === 'pending-removal'
                            ? 'Pending removal from the actual portfolio'
                            : pendingType === 'pending-addition'
                                ? `Pending addition: ${state.quantity} shares`
                                : pendingType === 'pending-edit'
                                    ? `Pending share change: ${row?.actual_quantity ?? ''} to ${state.quantity}`
                                    : state.authoritativeChecked
                                        ? `In actual portfolio: ${state.quantity} shares`
                                        : 'Add to the actual portfolio'
                    );
                    return (
                        <div
                            data-portfolio-state={pendingType}
                            className="relative flex w-full min-w-0 items-center justify-center"
                        >
                            <KitTooltip content={tooltipTitle}>
                                <span>
                                    <Checkbox
                                        checked={state.checked}
                                        disabled={disabled}
                                        tone={state.draft ? 'warning' : 'accent'}
                                        readOnly
                                        aria-label={`Included in actual portfolio: ${securityLabel}`}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (disabled) return;
                                            if (!state.checked && !state.authoritativeChecked) {
                                                openPortfolioQuantityEditor(row, event.currentTarget);
                                                return;
                                            }
                                            handlePortfolioMembershipChange(row, !state.checked);
                                        }}
                                    />
                                </span>
                            </KitTooltip>
                            {state.checked && (
                                <KitTooltip content={`Edit shares for ${securityLabel}`}>
                                    <KitIconButton
                                        label={`Edit shares for ${securityLabel}`}
                                        disabled={disabled}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            openPortfolioQuantityEditor(row, event.currentTarget);
                                        }}
                                        // An 18px button beside the checkbox: shown for a
                                        // draft (in MUI's warning orange), else on hover or
                                        // keyboard focus.
                                        className={cx(
                                            'absolute left-[calc(50%+18px)] top-1/2 !h-[18px] !w-[18px] -translate-y-1/2',
                                            'transition-[opacity,color] duration-[120ms] ease-[ease]',
                                            '[.portfolio-membership-cell:hover_&]:opacity-100 focus-visible:opacity-100',
                                            state.draft
                                                ? 'opacity-100 enabled:!text-[#ed6c02] enabled:dark:!text-[#ffa726]'
                                                : 'opacity-0 enabled:!text-content-secondary',
                                            'enabled:hover:!text-ui-ink enabled:dark:hover:!text-ui-ink',
                                        )}
                                    >
                                        <Pencil aria-hidden="true" size={13} />
                                    </KitIconButton>
                                </KitTooltip>
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'identity_summary',
                // The field's name and its picker. The header sorts on click
                // and moves on arrow keys; the picker and its menu (rendered
                // outside, but bubbling through React) keep theirs.
                header: () => (
                    <span className="flex w-full min-w-0 items-center justify-between gap-0.5">
                        <span className="truncate">{securityIdentityFieldLabel(identityDisplayField)}</span>
                        <span className="inline-flex" onClick={stopPropagation} onKeyDown={stopPropagation}>
                            <SecurityIdentityFieldMenu
                                value={identityDisplayField}
                                onChange={setIdentityDisplayField}
                            />
                        </span>
                    </span>
                ),
                size: 250,
                minSize: 190,
                accessorFn: (row) => securityIdentityPresentation(row, identityDisplayField).value,
                sortFn: sortByText,
                meta: {
                    label: WATCHLIST_IDENTITY_FIELD_OPTIONS.find(
                        (option) => option.value === identityDisplayField,
                    )?.label || 'Company Name',
                    disableColumnMenu: true,
                    headerClassName: watchlistColumnGroupClass('identity'),
                    cellClassName: watchlistColumnGroupClass('identity'),
                },
                cell: ({ row: { original: row } }) => {
                    const presentation = securityIdentityPresentation(
                        row,
                        identityDisplayField,
                    );
                    return (
                        <div
                            className={cx(
                                'w-full min-w-0',
                                identityDisplayField === 'submitted_identifier' && '[font-family:monospace]',
                            )}
                        >
                            <SecurityDetailsPopover
                                row={row}
                                loading={Boolean(loading)}
                                displayLabel={presentation.value}
                                labelTooltip={presentation.tooltip}
                                identityDetails={securityIdentityDetails(row)}
                                triggerAriaLabel={`Security and market data details for ${presentation.value}`}
                            />
                        </div>
                    );
                },
            },
            {
                id: 'country',
                header: 'Country',
                size: 105,
                accessorFn: (row) => String(row?.country || '').trim() || '—',
                sortFn: sortByText,
                meta: {
                    headerClassName: watchlistColumnGroupClass('identity'),
                    cellClassName: watchlistColumnGroupClass('identity'),
                },
                // The flag and code, as in the app's other tables; copying a
                // cell still gives the plain code.
                cell: ({ getValue }) => {
                    const country = getValue();
                    return country === '—' ? country : <CountryFlagText country={country} />;
                },
            },
            ...editableWatchlistAttributeColumns,
            ...ownGroupColumns,
            {
                id: 'price',
                header: 'Price ($)',
                size: 110,
                accessorFn: (row) => row.price,
                sortFn: sortByMagnitude,
                meta: {
                    sortKind: 'number',
                    formatText: twoDecimalFormatter,
                    headerClassName: watchlistColumnGroupClass('market', { start: true }),
                    cellClassName: ({ row: { original: row } }) => {
                        const direction = row?._priceFlashDirection;
                        return joinClassNames(
                            watchlistColumnGroupClass('market', { start: true }),
                            direction === 'up' ? 'watchlist-live-price-up' : null,
                            direction === 'down' ? 'watchlist-live-price-down' : null,
                        );
                    },
                },
                cell: ({ row: { original: row }, getValue }) => {
                    if (isWatchlistMarketDataPending(row)) {
                        return renderPendingMarketDataValueCell(row);
                    }
                    const formattedValue = twoDecimalFormatter(getValue());
                    if (!formattedValue) {
                        return '';
                    }

                    const tooltipTitle = formatPriceTooltip(row);
                    if (!tooltipTitle) {
                        return formattedValue;
                    }

                    return renderValueWithTooltip(formattedValue, tooltipTitle);
                },
            },
            {
                id: 'local_price',
                header: 'Local Px',
                size: 136,
                accessorFn: (row) => row.local_price,
                sortFn: sortByMagnitude,
                meta: {
                    sortKind: 'number',
                    // Copied text matches the cell.
                    formatText: (_value, row) => formatLocalPrice(row?.local_price, row?.local_currency),
                    headerClassName: watchlistColumnGroupClass('market'),
                    cellClassName: watchlistColumnGroupClass('market'),
                },
                cell: ({ row: { original: row } }) => {
                    if (isWatchlistMarketDataPending(row)) {
                        return renderPendingMarketDataValueCell(row);
                    }
                    const value = formatLocalPrice(row?.local_price, row?.local_currency);
                    return value ? renderValueWithTooltip(value, formatPriceTooltip(row)) : '';
                },
            },
            {
                id: 'market_status',
                header: 'Market',
                size: 120,
                accessorFn: (row) => {
                    const marketStatus = normalizeMarketStatus(row?.market_status);
                    if (marketStatus) return marketStatus;
                    if (isWatchlistMarketRegistrationPending(row)) return 'searching';
                    if (isWatchlistMarketDataPending(row)) return 'fetching';
                    if (isWatchlistMarketDataRetrying(row)) return 'retrying';
                    if (isWatchlistMarketDataUnavailable(row)) return 'unavailable';
                    return 'awaiting';
                },
                sortFn: sortByMarketStatus,
                meta: {
                    sortKind: 'marketStatus',
                    formatText: (value, row) => (
                        isWatchlistReplacementPending(row)
                            ? 'Removing'
                            : MARKET_STATUS_TEXT[value] || getWatchlistMarketStatusLabel(row) || 'Awaiting status'
                    ),
                    headerClassName: watchlistColumnGroupClass('market'),
                    cellClassName: watchlistColumnGroupClass('market'),
                },
                cell: ({ row: { original: row } }) => {
                    if (isWatchlistReplacementPending(row)) {
                        return renderStatusChip(
                            'warning',
                            'Removing',
                            'This security is being removed or replaced after your Security Resolution change.',
                        );
                    }

                    const marketStatus = normalizeMarketStatus(row.market_status);
                    if (marketStatus) {
                        const isOpen = marketStatus === 'open';
                        return renderStatusChip(
                            isOpen ? 'success' : 'danger',
                            isOpen ? MARKET_STATUS_TEXT.open : MARKET_STATUS_TEXT.closed,
                            marketDataUpdateTooltip(row.market_status_asof),
                        );
                    }

                    if (isWatchlistMarketRegistrationPending(row)) {
                        return renderStatusChip(
                            'info',
                            MARKET_STATUS_TEXT.searching,
                            "Searching for the listing's market-data symbol and latest available quote.",
                        );
                    }

                    if (isWatchlistMarketDataPending(row)) {
                        return renderStatusChip(
                            'info',
                            MARKET_STATUS_TEXT.searching,
                            getWatchlistMarketStatusTooltip(row),
                        );
                    }

                    if (isWatchlistMarketDataRetrying(row)) {
                        return renderStatusChip(
                            'warning',
                            MARKET_STATUS_TEXT.retrying,
                            getWatchlistMarketStatusTooltip(row),
                        );
                    }

                    if (isWatchlistMarketDataUnavailable(row)) {
                        return renderStatusChip(
                            'danger',
                            MARKET_STATUS_TEXT.unavailable,
                            getWatchlistMarketStatusTooltip(row),
                        );
                    }

                    const fallbackLabel = getWatchlistMarketStatusLabel(row) || 'Awaiting status';
                    return renderStatusChip(
                        'neutral',
                        fallbackLabel,
                        getWatchlistMarketStatusTooltip(row) || 'Waiting for market-data status from the price loader.',
                    );
                },
            },
            {
                id: '_newsCell',
                header: 'News',
                // W7.2 (2026-08-03) bumped 76 → 88 to give the new badge-anchored
                // composition (24-px icon + top-right pill overlay + bottom-right
                // recency dot) breathing room for the widest pills without
                // wrapping or DataGrid-clipping the overlays.
                // W8.1 (2026-08-06) kept 88 — the tenths-precision pill
                // (`-15.1%` vs `-15.10%`) plus the slightly larger compact
                // font/padding still fit here with margin to spare.
                size: 88,
                enableSorting: false,
                accessorFn: (row) => (row?._newsArticleCount ?? 0),
                meta: {
                    headerClassName: watchlistColumnGroupClass('market'),
                    cellClassName: watchlistColumnGroupClass('market'),
                },
                cell: ({ row: { original: row } }) => {
                    const item = row?._newsItem;
                    if (!item) return null;
                    return (
                        <NewsCell
                            item={item}
                            moverThresholdPct={newsMoverThresholdPct}
                            lastUpdatedAt={newsLastUpdatedAt}
                            recencyWindowHours={newsRecencyWindowHours}
                            readArticleIds={newsReadArticleIds}
                            onArticlesRead={onNewsArticleRead}
                            deepDiveFocusAreas={deepDiveFocusAreas}
                            onLoadNewsDetails={onLoadNewsDetails}
                            securityArticles={row?._securityDetailArticles}
                            securityArticlesLoading={row?._securityDetailLoading}
                            securityArticlesError={row?._securityDetailError}
                        />
                    );
                },
            },
            {
                id: 'price_quote_at_sort',
                header: 'Quoted',
                size: 118,
                accessorFn: (row) => row.price_quote_at_sort,
                sortFn: sortByTimestamp,
                meta: {
                    sortKind: 'date',
                    formatText: (_value, row) => row?.price_quote_at_display || '',
                    headerClassName: watchlistColumnGroupClass('market'),
                    cellClassName: watchlistColumnGroupClass('market'),
                },
                cell: ({ row: { original: row } }) => {
                    const label = row?.price_quote_at_display || '';
                    if (!label) {
                        return '';
                    }

                    const quotedLabel = row?.price_quote_at_full || label;
                    const ingestedLabel = formatTimestampDisplay(
                        row?._displayIngestedAt ?? row?.updated_at,
                        { includeYear: true },
                    );
                    const tooltipTitle = ingestedLabel && ingestedLabel !== quotedLabel
                        ? `${quotedLabel} • Ingested ${ingestedLabel}`
                        : quotedLabel;
                    return renderValueWithTooltip(label, tooltipTitle);
                },
            },
            {
                id: 'beta',
                header: 'Beta',
                size: 100,
                accessorFn: (row) => row.beta,
                sortFn: sortByMagnitude,
                meta: {
                    sortKind: 'number',
                    formatText: twoDecimalFormatter,
                    headerClassName: watchlistColumnGroupClass('market'),
                    cellClassName: watchlistColumnGroupClass('market'),
                },
                cell: ({ row: { original: row }, getValue }) => {
                    if (isWatchlistMarketDataPending(row)) {
                        return renderPendingMarketDataValueCell(row);
                    }
                    return renderMarketMetricValue(row, 'beta', twoDecimalFormatter(getValue()));
                },
            },
            {
                id: 'adtv',
                header: 'ADTV (shares)',
                size: 132,
                accessorFn: (row) => row.adtv,
                sortFn: sortByMagnitude,
                meta: {
                    sortKind: 'number',
                    formatText: integerWithCommasFormatter,
                    headerClassName: watchlistColumnGroupClass('market'),
                    cellClassName: watchlistColumnGroupClass('market'),
                },
                cell: ({ row: { original: row }, getValue }) => {
                    if (isWatchlistMarketDataPending(row)) {
                        return renderPendingMarketDataValueCell(row);
                    }
                    return renderMarketMetricValue(row, 'adtv', integerWithCommasFormatter(getValue()));
                },
            },
            // Expected return is not shown (or exported) until a return
            // forecast populates it.
            {
                id: 'Idio Vol (%)',
                header: 'Idio Vol (%)',
                size: 150,
                accessorFn: (row) => row['Idio Vol (%)'],
                sortFn: sortByMagnitude,
                meta: {
                    sortKind: 'number',
                    formatText: percentFormatter,
                    headerClassName: watchlistColumnGroupClass('risk', { start: true }),
                    cellClassName: watchlistColumnGroupClass('risk', { start: true }),
                },
            },
            {
                id: 'Factor Vol (%)',
                header: 'Factor Vol (%)',
                size: 150,
                accessorFn: (row) => row['Factor Vol (%)'],
                sortFn: sortByMagnitude,
                meta: {
                    sortKind: 'number',
                    formatText: percentFormatter,
                    headerClassName: watchlistColumnGroupClass('risk'),
                    cellClassName: watchlistColumnGroupClass('risk'),
                },
            },
            {
                id: 'Total Vol (%)',
                header: 'Total Vol (%)',
                size: 150,
                accessorFn: (row) => row['Total Vol (%)'],
                sortFn: sortByMagnitude,
                meta: {
                    sortKind: 'number',
                    formatText: percentFormatter,
                    headerClassName: watchlistColumnGroupClass('risk'),
                    cellClassName: watchlistColumnGroupClass('risk'),
                },
            },
            // The four windows show one basis; a missing return is blank.
            ...TRAILING_RETURN_WINDOWS.map((returnWindow, index) => ({
                id: returnWindow.columnId,
                header: () => (
                    <WatchlistReturnsColumnHeader
                        label={returnWindow.columnId}
                        basis={returnsBasis}
                        selectorId={returnWindow.columnId === returnsControlColumnId
                            ? WATCHLIST_RETURNS_SELECTOR_ID
                            : null}
                        anchorEl={returnsHeaderAnchorEl}
                        onOpen={setReturnsHeaderAnchorEl}
                    />
                ),
                // Room for the longest basis label beside the menu button.
                size: 160,
                accessorFn: (row) => row[returnsFieldName(returnWindow.key, returnsBasis)],
                sortFn: sortByNumber,
                meta: {
                    label: returnWindow.columnId,
                    sortKind: 'number',
                    formatText: percentFormatter,
                    headerClassName: watchlistColumnGroupClass('returns', { start: index === 0 }),
                    cellClassName: watchlistColumnGroupClass('returns', { start: index === 0 }),
                },
            })),
            // Market health as an icon, sorted most severe first; copy and the
            // tooltip use its word.
            {
                id: 'market_health',
                header: () => <HeaderTitle label="Health" />,
                size: 84,
                sortDescFirst: true,
                accessorFn: (row) => marketHealthSeverity(row.market_health),
                sortFn: sortByNumber,
                meta: {
                    label: 'Market health',
                    align: 'center',
                    headerAlign: 'center',
                    sortKind: 'number',
                    formatText: (_value, row) => MARKET_HEALTH_LABELS[marketHealthStatus(row?.market_health)],
                },
                cell: ({ row: { original: row } }) => (
                    <WatchlistMarketHealthChip health={row.market_health} canRemove={Boolean(row.delete_group_control && row.delete_group_token)} />
                ),
            },
            {
                id: 'actions',
                header: '',
                size: 52,
                minSize: 52,
                maxSize: 52,
                enableCellSelection: false,
                meta: {
                    align: 'center',
                    headerAlign: 'center',
                    disableColumnMenu: true,
                },
                cell: ({ row: { original: row } }) => {
                    if (!row?.delete_group_token || row?.delete_group_control === false) {
                        return null;
                    }
                    const token = String(row.delete_group_token);
                    const staged = stagedDeletionTokens.has(token);
                    const isDisabled = isOperationInProgress;
                    const tooltipTitle = isDisabled
                        ? 'A watchlist update is currently in progress.'
                        : staged
                            ? 'Undo staged removal'
                            : row.delete_will_remove_portfolio
                                ? 'Stage removal from Watchlist and portfolio'
                                : 'Stage removal from Watchlist';

                    return (
                        <KitTooltip content={tooltipTitle}>
                            <span>
                                <KitIconButton
                                    disabled={isDisabled}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        toggleDeletionDraft(row);
                                    }}
                                    label={tooltipTitle}
                                    // Secondary text, MUI's error red when staged and
                                    // under a pointer.
                                    className={cx(
                                        staged
                                            ? 'enabled:!text-[#d32f2f] enabled:dark:!text-[#f44336]'
                                            : 'enabled:!text-content-secondary',
                                        'enabled:hover:!text-[#d32f2f] enabled:dark:hover:!text-[#f44336]',
                                    )}
                                >
                                    {/* MUI's small icon size, smaller with the compact type. */}
                                    {staged
                                        ? <Undo2 aria-hidden="true" size={20} className="[[data-compact]_&]:size-[17.14px]" />
                                        : <Trash2 aria-hidden="true" size={20} className="[[data-compact]_&]:size-[17.14px]" />}
                                </KitIconButton>
                            </span>
                        </KitTooltip>
                    );
                },
            },
        ];
    }, [
        editableWatchlistAttributeColumns,
        ownGroupColumns,
        factorModelLabel,
        handlePortfolioMembershipChange,
        identityDisplayField,
        isOperationInProgress,
        loading,
        newsLastUpdatedAt,
        newsMoverThresholdPct,
        newsReadArticleIds,
        newsRecencyWindowHours,
        onNewsArticleRead,
        deepDiveFocusAreas,
        onLoadNewsDetails,
        openPortfolioQuantityEditor,
        portfolioSaving,
        portfolioStateForRow,
        returnsBasis,
        returnsControlColumnId,
        returnsHeaderAnchorEl,
        stagedDeletionTokens,
        toggleDeletionDraft,
        watchlistSaveInFlight,
    ]);
    // The download leads with the identity columns the table can show:
    // company_name, ticker, one column per submitted identifier type (e.g.
    // "sedol"), then country. They replace the raw identity fields. The
    // return columns carry the basis the table shows, named for it.
    const watchlistExport = useMemo(() => {
        const submittedColumns = new Set();
        const rows = rowData.map((sourceRow) => {
            const {
                id,
                _isImputedFactorData,
                _missingFactorStatus,
                _missingPriceData,
                _priceFlashDirection,
                _displayPriceSource,
                _displayPriceAsOfDate,
                _displayQuoteTimestamp,
                _displayIngestedAt,
                _newsItem,
                _newsArticleCount,
                _securityDetailArticles,
                _securityDetailLoading,
                _securityDetailError,
                price_quote_at_sort,
                in_actual_portfolio,
                actual_quantity,
                portfolio_selection_token,
                portfolio_state_hash,
                portfolio_editability,
                portfolio_pending,
                pending_in_actual_portfolio,
                pending_quantity,
                uploaded_ticker,
                msi_listing_label,
                delete_group_token,
                delete_will_remove_portfolio,
                expectedreturn,
                company_name,
                ticker,
                country,
                sedol,
                inbound_id_type,
                inbound_id_value,
                display_identifier_type,
                display_identifier,
                ...row
            } = sourceRow;
            const identity = securityExportIdentity(sourceRow);
            if (identity.submittedColumn) submittedColumns.add(identity.submittedColumn);
            return {
                ...withReturnsExportColumns(row, returnsBasis),
                ...Object.fromEntries(genericAttributeDefinitions.map((definition) => [
                    `Attribute: ${definition.display_name}`,
                    genericAttributeValue(row, definition.attribute_definition_id) || null,
                ])),
                ...Object.fromEntries(ownGroupColumns.filter((column) => columnVisibility[column.id] !== false)
                    .map((column) => [column.header, column.accessorFn(sourceRow)])),
                ...identity.columns,
            };
        });
        return {
            rows,
            header: ['company_name', 'ticker', ...submittedColumns, 'country'],
        };
    }, [
        genericAttributeDefinitions,
        genericAttributeValue,
        ownGroupColumns,
        columnVisibility,
        returnsBasis,
        rowData,
    ]);
    // The rows the quick filter and the column filters keep (see above).
    // While loading, the grid shows its own skeleton rows in their place.
    const gridRows = categoryTools.visibleRows;

    // The "+ Add security" row below the rows, and the securities picked in
    // it as pending rows above it until Save stages them
    // (stagePickedAdditions). A Watchlist member found again is shown
    // ("Show it"): the quick filter finds it and no column filter hides it.
    const publishedCogniveIds = useMemo(
        () => new Set(normalizeCogniveIdList(effectiveRows.map((row) => row?.cognive_id))),
        [effectiveRows],
    );
    const showInWatchlist = useCallback((security) => {
        const ticker = String(security?.ticker || '').trim();
        if (!ticker) return;
        setQuickFilterQuery(ticker);
        setQuickFilterKey((key) => key + 1);
        setCategoryFilters({});
    }, []);
    const addSecurityRow = useAddSecurityGridRows({
        picks,
        onPicksChange: applyPicksAction,
        publishedCount: effectiveRows.length,
        capacityKey: watchlistResolutionRefreshKey,
        existingCogniveIds: publishedCogniveIds,
        disabled: publicationSnapshotLocked || watchlistSaveInFlight,
        onExisting: showInWatchlist,
        onUndo: undoDraftStep,
        onRedo: redoDraftStep,
    });
    // Until something is picked, an empty Watchlist also invites a pasted sheet.
    const showEmptyPasteHint = rowData.length === 0 && !isInitialLoading && !additionsDirty;
    const trailingRowCount = picks.length + 1;

    // All rows share one scrolling list in a frame that reaches the bottom of
    // the window (or fits a short list), so the rows have the only vertical
    // scrollbar and the header stays in view.
    const [gridFrameRef, gridFrameAvailableHeight] = useViewportFillHeight();
    let frameRowCount = Math.max(gridRows.length, 1) + trailingRowCount;
    if (rowData.length === 0) frameRowCount = showEmptyPasteHint ? EMPTY_WATCHLIST_ROW_COUNT : trailingRowCount;
    const watchlistContainerHeight = gridFrameHeight(
        isInitialLoading
            ? WATCHLIST_LOADING_HEIGHT
            : 40 + (frameRowCount * 38) + 2,
        gridFrameAvailableHeight,
    );

    // Edits reach every Watchlist row by id, so a category rename also
    // covers rows the filters hide.
    const watchlistSpreadsheet = rowData.length > 0 ? {
        isCellEditable: isGenericAttributeCellEditable,
        applyEdits: (edits, label) => applyGenericAttributeDraftBatch(edits.flatMap(({ rowId, columnId, value }) => {
            const row = rowDataById.get(rowId);
            const definition = attributeDefinitionsByField.get(columnId);
            return row && definition ? [{ row, definition, value }] : [];
        }), label),
        undo: undoDraftStep,
        redo: redoDraftStep,
        getCellEditorProps: (rowId, field) => {
            const definition = attributeDefinitionsByField.get(field);
            return {
                label: definition?.display_name,
                inputLabel: `Edit ${definition?.display_name} for ${securityCompanyName(rowDataById.get(rowId))}`,
                // One spelling per category, most used first.
                options: definition ? categoryTools.getEditorOptions(definition.attribute_definition_id) : [],
            };
        },
        readOnlyHint: genericAttributeEditingEnabled
            ? 'Only attribute columns can be edited.'
            : 'Editing is paused while the Watchlist saves or refreshes.',
        interceptPaste: importPastedSheet,
    } : null;
    const draftHistoryActive = draftHistory.past.length > 0 || draftHistory.future.length > 0;

    const handleDownload = () => {
        const ws = XLSX.utils.json_to_sheet(watchlistExport.rows, { header: watchlistExport.header });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Watchlist');
        XLSX.writeFile(wb, 'watchlist.xlsx');
    };

    return (
        // The attribute column headers read their filter state from here.
        <CategoryColumnsContext.Provider value={categoryTools.headerContext}>
        <Grid2 size={{md: 12 }}>
            <Paper ref={watchlistPageRef} className={cardClasses} elevation={1} sx={watchlistPanelSx}>
            <WatchlistPendingCallout anchorEl={historyButtonEl}
                message={active ? pendingGuidance?.message : null}
                onDismiss={() => setPendingGuidance(null)}
                onReview={() => openHistoryPanel({ revisionId: pendingGuidance?.revisionId })} />
            <WatchlistGetStartedCallout
                open={Boolean(showUploadGuide && active)}
                anchorEl={toolbarActionsEl}
                onUpload={() => openHistoryPanel()}
                onPaste={() => setPasteDialogOpen(true)}
                onAddSecurity={() => setAddSecurityOpen(true)}
                addSecurityDisabled={publicationSnapshotLocked || watchlistSaveInFlight}
                onDismiss={onDismissGuide}
            />
            <DashboardToolbarPortal active={active}>
                <Box display="flex" alignItems="center" gap={0.75} flexWrap="nowrap">
                    <WatchlistMarketStatusChip {...marketStatusSummary} />
                    <Divider orientation="vertical" flexItem sx={appBarDividerSx} />
                    <SecurityOperationProgress
                        statusDoc={publicationNeedsAttention ? null : activeOperationStatus}
                        uploading={replacingWatchlist}
                        contextUpdate={watchlistContextSync?.progressContext}
                    />
                    {unresolvedWatchlistCount > 0 && (
                        <Tooltip
                            title="Check User Settings → Security Resolution for details and how to resolve."
                            arrow
                        >
                            <Chip
                                size="small"
                                color="error"
                                label={`${unresolvedWatchlistCount} Unresolved`}
                                sx={SOFT_FILLED_CHIP_SX}
                            />
                        </Tooltip>
                    )}
                    {factorContextUnavailable && rowData.length > 0 && (
                        <Tooltip
                            arrow
                            title="Open Watchlist History to prepare the required context validation."
                        >
                            <Chip
                                size="small"
                                color="warning"
                                label="Validation required"
                                sx={SOFT_FILLED_CHIP_SX}
                                data-testid="factor-context-validation-chip"
                            />
                        </Tooltip>
                    )}
                    {!factorContextUnavailable && factorMatricesUnavailable && rowData.length > 0 && (
                        <Tooltip
                            arrow
                            title={`Factor data is unavailable for ${factorModelLabel || 'the active model'} in the current matrix load. Risk columns will populate when coverage becomes available.`}
                        >
                            <Chip
                                size="small"
                                color="warning"
                                label="Factor data unavailable"
                                sx={SOFT_FILLED_CHIP_SX}
                                data-testid="factor-data-unavailable-chip"
                            />
                        </Tooltip>
                    )}
                    {(((factorContextUnavailable || factorMatricesUnavailable) && rowData.length > 0) || imputedCount > 0 || ignoredCount > 0 || noPriceCount > 0) && (
                        <>
                            {imputedCount > 0 && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    label={`${imputedCount} unsupported estimates`}
                                    sx={SOFT_FILLED_CHIP_SX}
                                />
                            )}
                            {ignoredCount > 0 && (
                                <Chip
                                    size="small"
                                    color="default"
                                    label={`${ignoredCount} ignored`}
                                    sx={SOFT_FILLED_CHIP_SX}
                                />
                            )}
                            {noPriceCount > 0 && (
                                <Chip
                                    size="small"
                                    color="info"
                                    label={`${noPriceCount} no price`}
                                    sx={SOFT_FILLED_CHIP_SX}
                                />
                            )}
                            <Tooltip
                                arrow
                                title={factorStatusTooltipTitle}
                            >
                                <IconButton
                                    size="small"
                                    aria-label="About missing factor data handling"
                                    sx={{
                                        color: 'text.secondary',
                                        p: 0.25,
                                    }}
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    <WatchlistQuoteStatusIndicator
                        pendingCount={pendingMarketDataCount}
                        retryingCount={retryingMarketDataCount}
                        liveQuotesLastUpdatedAt={liveQuotesLastUpdatedAt}
                        hasRows={rowData.length > 0}
                    />
                    {rowData.length > 0
                        && (portfolioEditor?.conflict_summary?.count ?? 0) > 0 && (
                        <Tooltip
                            arrow
                            title={portfolioEditor?.conflict_summary?.message || 'Some current holdings are locked and will be preserved unchanged.'}
                        >
                            <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={`${portfolioEditor.conflict_summary.count} holding${portfolioEditor.conflict_summary.count === 1 ? '' : 's'} preserved`}
                            />
                        </Tooltip>
                    )}
                </Box>
                <Box ref={setToolbarActionsEl} sx={{ ...workspaceActionClusterSx, ml: 0, flexWrap: 'nowrap', gap: 0.75 }}>
                    {draftHistoryActive && (
                        <>
                            <Tooltip title={`Undo (${UNDO_SHORTCUT_LABEL})`}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => { undoDraftStep(); }}
                                        disabled={!canStepDraftHistory(draftHistory.past[draftHistory.past.length - 1])}
                                        aria-label="Undo change"
                                        sx={appBarActionButtonSx}
                                    >
                                        <UndoIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={`Redo (${REDO_SHORTCUT_LABEL})`}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => { redoDraftStep(); }}
                                        disabled={!canStepDraftHistory(draftHistory.future[0])}
                                        aria-label="Redo change"
                                        sx={appBarActionButtonSx}
                                    >
                                        <RedoIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                    {(genericAttributesDirty || portfolioDirty || deletionDirty || additionsDirty) && (
                        <>
                            <Tooltip title="Save changes">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => { void savePendingWatchlistChanges(); }}
                                        disabled={
                                            genericAttributeSaving
                                            || portfolioSaving
                                            || deletionSaving
                                            || watchlistSaveInFlight
                                            || publicationSnapshotLocked
                                            || stalePicks
                                            || (retainedGenericAttributesDirty && !watchlistFieldEditorReady)
                                            || (retainedPortfolioDraftDirty && (
                                                !portfolioStateHash
                                                || portfolioDraftStale
                                                || invalidRetainedPortfolioRows.length > 0
                                            ))
                                        }
                                        aria-label="Save changes"
                                        data-testid="watchlist-save-changes"
                                        sx={[
                                            appBarActionButtonSx,
                                            {
                                                backgroundColor: 'primary.main',
                                                color: 'primary.contrastText',
                                                '&:hover': { backgroundColor: 'primary.dark' },
                                            },
                                        ]}
                                    >
                                        {(
                                            genericAttributeSaving
                                            || portfolioSaving
                                            || deletionSaving
                                            || watchlistSaveInFlight
                                        )
                                            ? <CircularProgress size={15} thickness={5} color="inherit" />
                                            : <SaveOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Discard changes">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={clearPendingWatchlistChanges}
                                        disabled={
                                            genericAttributeSaving
                                            || portfolioSaving
                                            || deletionSaving
                                            || watchlistSaveInFlight
                                            || (retainedGenericAttributesDirty && publicationSnapshotLocked)
                                        }
                                        aria-label="Discard changes"
                                        sx={appBarActionButtonSx}
                                    >
                                        <UndoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                    {categoryTools.chips}
                    <WatchlistQuickFilter
                        key={quickFilterKey}
                        query={quickFilterQuery}
                        onQueryChange={setQuickFilterQuery}
                        matchCount={rowData.length > 0 ? gridRows.length : 0}
                        totalCount={rowData.length}
                        disabled={rowData.length === 0}
                    />
                    <Tooltip title="Add security to Watchlist">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => setAddSecurityOpen(true)}
                                aria-label="Add security to Watchlist"
                                sx={appBarActionButtonSx}
                                disabled={publicationSnapshotLocked || watchlistSaveInFlight}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Manage Watchlist attribute columns">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => setAttributeManagerOpen(true)}
                                aria-label="Manage Watchlist attribute columns"
                                sx={appBarActionButtonSx}
                                disabled={
                                    publicationSnapshotLocked
                                    || !watchlistFieldEditorReady
                                    || !watchlistFieldEditor.attributeSnapshot
                                }
                            >
                                <ViewColumnOutlinedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Watchlist imports and history">
                        <IconButton
                            size="small"
                            onClick={() => openHistoryPanel()}
                            aria-label="Open watchlist imports"
                            ref={setHistoryButtonEl}
                            sx={appBarActionButtonSx}
                        >
                            <HistoryBookIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Download watchlist details">
                        <IconButton
                            size="small"
                            onClick={handleDownload}
                            aria-label="Download watchlist details"
                            sx={appBarActionButtonSx}
                            disabled={rowData.length === 0}
                        >
                            <FileDownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DashboardToolbarPortal>

            {watchlistFieldEditor.error && (
                <Alert
                    severity="warning"
                    sx={{ mb: 0.75, py: 0.25 }}
                    onClose={() => setWatchlistFieldEditor((current) => ({
                        ...current,
                        error: '',
                    }))}
                >
                    {watchlistFieldEditor.error}
                </Alert>
            )}

            {genericAttributeError && (
                <Alert
                    severity="warning"
                    sx={{ mb: 0.75, py: 0.25 }}
                    onClose={() => setGenericAttributeError('')}
                >
                    {genericAttributeError}
                </Alert>
            )}

            {categoryLimitNoticeOpen && categoryTools.saveBlockedMessage && (
                <Alert
                    severity="error"
                    sx={{ mb: 0.75, py: 0.25 }}
                    onClose={() => setCategoryLimitNoticeOpen(false)}
                >
                    {`Not saved. ${categoryTools.saveBlockedMessage}`}
                </Alert>
            )}

            {deleteErrorMessage && (
                <Alert
                    severity="error"
                    sx={{ mb: 0.75, py: 0.25 }}
                    onClose={() => setDeleteErrorMessage('')}
                >
                    {deleteErrorMessage}
                </Alert>
            )}

            {additionErrorMessage && (
                <Alert
                    severity="error"
                    sx={{ mb: 0.75, py: 0.25 }}
                    onClose={() => setAdditionErrorMessage('')}
                >
                    {additionErrorMessage}
                </Alert>
            )}

            {stalePicks && (
                <Alert severity="warning" sx={{ mb: 0.75, py: 0.25 }}>
                    {STALE_ADDITIONS_MESSAGE}
                </Alert>
            )}

            {portfolioDraftStale && retainedPortfolioDraftDirty && (
                <Alert severity="warning" sx={{ mb: 0.75, py: 0.25 }}>
                    The actual portfolio or Watchlist changed while you were editing. Discard changes and review the current rows before saving.
                </Alert>
            )}

            {ownGroupNotice && (
                <Alert severity="warning" sx={{ mb: 0.75, py: 0.25 }}>
                    {`Own-group statistics are unavailable. ${ownGroupNotice}`}
                </Alert>
            )}

            <WatchlistPublicationRecoveryNotice />

            <BusyOverlay
                show={
                    !isInitialLoading
                    && publicationSnapshotLocked
                    && !publicationNeedsAttention
                }
                minHeight={watchlistContainerHeight}
            >
                <Box
                    ref={gridFrameRef}
                    data-testid="watchlist-grid-frame"
                    sx={{ height: watchlistContainerHeight, position: 'relative' }}
                >
                    <WatchlistGrid
                        aria-label="Watchlist"
                        pinnedFields={activePinnedIdentityFields}
                        rows={gridRows}
                        columns={columns}
                        spreadsheet={watchlistSpreadsheet}
                        commandsRef={gridCommandsRef}
                        pendingRows={addSecurityRow.pendingRows}
                        addRow={addSecurityRow.addRow}
                        // An empty Watchlist shows its paste invitation instead.
                        noRowsLabel={rowData.length === 0 ? null : undefined}
                        height={watchlistContainerHeight}
                        columnHeaderHeight={40}
                        rowHeight={38}
                        loading={isInitialLoading}
                        columnVisibility={columnVisibility}
                        onColumnVisibilityChange={(updater) => {
                            setColumnVisibility((current) => {
                                const next = functionalUpdate(updater, current);
                                return areColumnVisibilitiesEqual(current, next)
                                    ? current
                                    : (next && typeof next === 'object' ? next : {});
                            });
                        }}
                        sorting={sorting}
                        onSortingChange={(updater) => {
                            setSortPreference((current) => {
                                const currentSorting = current.storageKey === sortStorageKey
                                    ? current.sorting
                                    : readPersistedSorting(sortStorageKey);
                                const nextSorting = functionalUpdate(updater, currentSorting);
                                if (areSortingsEqual(currentSorting, nextSorting)) {
                                    return current.storageKey === sortStorageKey
                                        ? current
                                        : { storageKey: sortStorageKey, sorting: currentSorting };
                                }
                                return { storageKey: sortStorageKey, sorting: nextSorting };
                            });
                        }}
                        getRowClassName={getRowClassName}
                    />
                    {showEmptyPasteHint && (
                        // Below the header and the add row.
                        <WatchlistEmptyPasteHint top={40 + 38} onPaste={() => setPasteDialogOpen(true)} />
                    )}
                </Box>
            </BusyOverlay>
        </Paper>
        </Grid2>
        {portfolioDialogOpen && (
            <PortfolioPositionsDialog
                rows={rowData}
                stateForRow={portfolioStateForRow}
                onMembershipChange={handlePortfolioMembershipChange}
                onQuantityChange={handlePortfolioQuantityChange}
                onSave={async () => {
                    if (await savePortfolioDraft()) onClosePortfolioDialog?.();
                }}
                onDiscard={clearPortfolioDraft}
                onClose={onClosePortfolioDialog}
                dirty={portfolioDirty}
                disabled={Boolean(
                    loading || replacingWatchlist || isOperationInProgress
                    || deletionDirty || !portfolioStateHash || !portfolioSnapshotFingerprint
                )}
                saving={portfolioSaving}
                stale={portfolioDraftStale}
                error={portfolioSaveError}
                blockedMessage={deletionDirty
                    ? 'Save or discard pending Watchlist security removals before editing portfolio positions.'
                    : portfolioEditor?.editing_unavailable_reason?.message}
            />
        )}
        <WatchlistAttributeManagerDialog
            open={attributeManagerOpen}
            definitions={genericAttributeDefinitions}
            busy={genericAttributeSaving || watchlistSaveInFlight}
            error={genericAttributeError}
            onClose={() => setAttributeManagerOpen(false)}
            onApply={applyGenericAttributeOperations}
            ownGroupPreferences={ownGroupPreferences}
            onOwnGroupChange={visibleOwnGroupState?.supported ? changeOwnGroupPreference : null}
            userId={user?.uid || ''}
            watchlistRevisionId={String(ownGroupSnapshot?.revision_id || '')}
            maintenanceBlockedReason={ownGroupBlockedReason}
            onFillBlanksWithAi={(recipe) => {
                if (ownGroupBlockedReason) return;
                setAttributeAiRecipe(recipe);
                setAttributeManagerOpen(false);
                setAttributeAiOpen(true);
            }}
            onCreateWithAi={() => {
                setAttributeAiRecipe(null);
                setAttributeManagerOpen(false);
                setAttributeAiOpen(true);
            }}
        />
        {active && user?.uid && (attributeManagerOpen || ownGroupAttributeIds.length > 0) && (
            <React.Suspense fallback={null}>
            <WatchlistOwnGroupStatistics
                owner={`${user.uid}:${sessionScopeToken || ''}`}
                snapshot={ownGroupSnapshot}
                cogniveIds={ownGroupCogniveIds}
                attributeIds={ownGroupAttributeIds}
                sourceHint={ownGroupSourceKey}
                viewKey={ownGroupViewKey}
                blockedReason={ownGroupBlockedReason}
                editorOpen={attributeManagerOpen}
                onState={setOwnGroupState}
            />
            </React.Suspense>
        )}
        {attributeAiOpen && (
            <React.Suspense fallback={<CircularProgress size={20} aria-label="Opening AI attribute editor" />}>
                <WatchlistAttributeAiDialog
                    open={attributeAiOpen}
                    onClose={() => setAttributeAiOpen(false)}
                    userId={user?.uid || ''}
                    expectedWatchlistRevisionId={String(watchlistFieldEditor.attributeSnapshot?.revision_id || '')}
                    attributeCount={genericAttributeDefinitions.length}
                    maintenanceRecipe={attributeAiRecipe}
                    onApplied={handleAttributeMutationResult}
                />
            </React.Suspense>
        )}
        <WatchlistAddSecurityDialog
            open={addSecurityOpen}
            onClose={() => setAddSecurityOpen(false)}
            expectedWatchlistRevisionId={String(
                watchlistFieldEditor.attributeSnapshot?.revision_id || '',
            )}
            publishedCount={effectiveRows.length}
            onReviewHandoff={typeof onReviewHandoff === 'function' ? handOffDialogAdditions : null}
        />
        <PasteFromExcelDialog
            open={pasteDialogOpen}
            destination="watchlist"
            onImport={importPastedSheet}
            onClose={() => setPasteDialogOpen(false)}
        />
        <WatchlistReturnsBasisSelector
            anchorEl={returnsHeaderAnchorEl}
            onClose={() => setReturnsHeaderAnchorEl(null)}
            selectorId={WATCHLIST_RETURNS_SELECTOR_ID}
            basis={returnsBasis}
            onChange={changeReturnsBasis}
            paperSx={watchlistPopoverPaperSx}
        />
        <Popover
            open={Boolean(portfolioQuantityEditor.anchorEl)}
            anchorEl={portfolioQuantityEditor.anchorEl}
            onClose={closePortfolioQuantityEditor}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
                sx: [watchlistPopoverPaperSx, { width: 208, p: 1 }],
            }}
        >
            <Box
                component="form"
                onSubmit={(event) => {
                    event.preventDefault();
                    applyPortfolioQuantityEditor();
                }}
            >
                <Typography variant="caption" sx={{ mb: 0.6, fontWeight: 700, display: 'block' }}>
                    Portfolio shares
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Shares"
                    value={portfolioQuantityEditor.value}
                    sx={cellEditorFieldSx}
                    error={
                        portfolioQuantityEditor.validationVisible
                        && !isValidPortfolioQuantity(portfolioQuantityEditor.value)
                    }
                    helperText={portfolioQuantityEditor.validationVisible
                        && !isValidPortfolioQuantity(portfolioQuantityEditor.value)
                        ? 'Enter a finite, nonzero number.'
                        : null}
                    inputProps={{
                        inputMode: 'decimal',
                        'aria-label': `Shares for ${portfolioQuantityEditor.row?.ticker || portfolioQuantityEditor.row?.display_identifier || 'security'}`,
                    }}
                    onChange={(event) => setPortfolioQuantityEditor((current) => ({
                        ...current,
                        value: event.target.value,
                        validationVisible: false,
                    }))}
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                            event.preventDefault();
                            closePortfolioQuantityEditor();
                        }
                    }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.4, mt: 0.6 }}>
                    <Tooltip title="Cancel">
                        <IconButton
                            size="small"
                            aria-label="Cancel portfolio shares"
                            onClick={closePortfolioQuantityEditor}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Confirm shares">
                        <IconButton
                            size="small"
                            type="submit"
                            color="primary"
                            aria-label="Confirm portfolio shares"
                        >
                            <CheckIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        </Popover>
        <Snackbar
            open={overrideFeedback.open}
            autoHideDuration={5000}
            onClose={() => setOverrideFeedback((current) => ({ ...current, open: false }))}
        >
            <Alert
                onClose={() => setOverrideFeedback((current) => ({ ...current, open: false }))}
                severity={overrideFeedback.severity}
                sx={{ width: '100%' }}
            >
                {overrideFeedback.message}
            </Alert>
        </Snackbar>

        <WatchlistHistoryPanel
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            initialRevisionRequest={historyRevisionRequest}
            // Every way to import a Watchlist sits together in the History header.
            uploadControl={(
                <>
                    {m365SyncControl}
                    <Tooltip title={`Paste from Excel (${PASTE_SHORTCUT_LABEL})`}>
                        <IconButton
                            size="small"
                            onClick={() => setPasteDialogOpen(true)}
                            aria-label="Paste from Excel"
                            sx={appBarActionButtonSx}
                        >
                            <ContentPasteOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {uploadControl}
                </>
            )}
            activeOperationStatus={activeOperationStatus}
            contextStatus={watchlistContextSync?.status}
            contextBusy={watchlistContextSync?.busy}
            onReviewContext={watchlistContextSync?.stageRefresh}
            onUseNewFactorData={watchlistContextSync?.applyUpdate}
            onDeclineContext={watchlistContextSync?.declineUpdate}
        />
        {categoryTools.dialogs}
        </CategoryColumnsContext.Provider>
    );
}
